import React, { useState, useEffect } from 'react';
import './App.css';

const API_URL = process.env.REACT_APP_BACKEND_URL || `${window.location.protocol}//${window.location.hostname}:8001`;
const COMPANY_NAME = process.env.REACT_APP_COMPANY_NAME || 'Votre Entreprise';

function App() {
  // Fonction utilitaire pour formater les nombres avec arrondis propres
  const formatNumber = (value, decimals = 2) => {
    if (value === null || value === undefined) return '0';
    return parseFloat(value).toFixed(decimals).replace(/\.?0+$/, '');
  };

  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [activeTab, setActiveTab] = useState('dashboard');
  const [clients, setClients] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [clientTypeFilter, setClientTypeFilter] = useState('');
  const [clientCaFilter, setClientCaFilter] = useState('');

  // États pour la recherche
  const [searchTermClients, setSearchTermClients] = useState('');
  const [searchTermProduits, setSearchTermProduits] = useState('');
  const [produits, setProduits] = useState([]);
  const [filteredProduits, setFilteredProduits] = useState([]);
  const [produitFamilleFilter, setProduitFamilleFilter] = useState('');
  const [commandeClientFilter, setCommandeClientFilter] = useState('');
  const [commandeSearchTerm, setCommandeSearchTerm] = useState('');
  const [commandes, setCommandes] = useState([]);
  const [users, setUsers] = useState([]);
  const [stockMovements, setStockMovements] = useState([]);
  const [stockAlerts, setStockAlerts] = useState([]);
  const [showStockHistory, setShowStockHistory] = useState(false);
  const [selectedProductForHistory, setSelectedProductForHistory] = useState(null);
  const [showStockForm, setShowStockForm] = useState(false);

  // États pour pagination
  const [currentPageClients, setCurrentPageClients] = useState(1);
  const [currentPageProduits, setCurrentPageProduits] = useState(1);
  const [currentPageCommandes, setCurrentPageCommandes] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [statistiques, setStatistiques] = useState(null);
  const [statistiquesProduits, setStatistiquesProduits] = useState(null);
  const [settings, setSettings] = useState(null);
  const [gestionStockActive, setGestionStockActive] = useState(true);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showBackToTop, setShowBackToTop] = useState(false); // NOUVEAU
  const [mobileUserMenuOpen, setMobileUserMenuOpen] = useState(false); // NOUVEAU
  const [notifications, setNotifications] = useState([]);
  
  // États pour les formulaires
  const [showLoginForm, setShowLoginForm] = useState(!token);
  const [showForgotPasswordForm, setShowForgotPasswordForm] = useState(false);
  const [showResetPasswordForm, setShowResetPasswordForm] = useState(false);
  const [resetToken, setResetToken] = useState('');
  const [showClientForm, setShowClientForm] = useState(false);
  const [showProduitForm, setShowProduitForm] = useState(false);
  const [showCommandeForm, setShowCommandeForm] = useState(false);
  const [showUserForm, setShowUserForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editingClient, setEditingClient] = useState(null);
  const [editingProduit, setEditingProduit] = useState(null);
  const [editingCommande, setEditingCommande] = useState(null);
  const [loginError, setLoginError] = useState('');
  
  // État pour les lignes de produits du formulaire commande
  const [lignesProduits, setLignesProduits] = useState([
    { id: Date.now(), produit_id: '', quantite: '', prix_personnalise: '' }
  ]);

  // Charger les données spécifiques à l'onglet actif
  useEffect(() => {
    if (activeTab === 'users' && currentUser?.role === 'admin') {
      loadUsers();
    }
  }, [activeTab, currentUser]);

  // Vérifier la connexion au démarrage
  useEffect(() => {
    if (token) {
      checkAuth();
    }
  }, [token]);

  useEffect(() => {
    if (isLoggedIn) {
      loadClients();
      loadProduits();
      loadCommandes();
      loadSettings();
      loadStockAlerts();
      if (currentUser?.role === 'admin') {
        loadUsers();
      }
      if (activeTab === 'statistiques') {
        loadStatistiques();
        loadStatistiquesProduits();
      }
    }
  }, [isLoggedIn, currentUser, activeTab]);

  // Réinitialiser la pagination quand le filtre client change
  useEffect(() => {
    setCurrentPageCommandes(1);
  }, [commandeClientFilter]);

  // Fonction pour afficher une notification toast
  const showNotification = (message, type = 'success') => {
    const id = Date.now();
    const newNotification = {
      id,
      message,
      type, // 'success', 'error', 'warning', 'info'
      timestamp: new Date()
    };
    
    setNotifications(prev => [...prev, newNotification]);
    
    // Supprimer automatiquement après 5 secondes
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== id));
    }, 5000);
  };

  // Fonction pour supprimer une notification
  const removeNotification = (id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  // Fonction pour rafraîchir tout le tableau de bord
  const refreshDashboard = async () => {
    try {
      await Promise.all([
        loadCommandes(),
        loadStatistiques(),
        loadProduits(), // Pour les stocks mis à jour
        loadStockAlerts() // Pour les nouvelles alertes
      ]);
      // Après le chargement, filtrer les clients pour mettre à jour les CA
      filterClients();
    } catch (error) {
      console.error('Erreur lors du rafraîchissement du tableau de bord:', error);
    }
  };

  const loadSettings = async () => {
    try {
      const response = await fetch(`${API_URL}/api/settings`);
      if (response.ok) {
        const data = await response.json();
        setSettings(data);
        setGestionStockActive(data.gestion_stock_active !== false); // Mettre à jour l'état local
        
        // Appliquer les couleurs dynamiquement
        if (data.couleur_primaire) {
          document.documentElement.style.setProperty('--primary-color', data.couleur_primaire);
          document.documentElement.style.setProperty('--primary-dark', adjustColor(data.couleur_primaire, -20));
          document.documentElement.style.setProperty('--primary-light', adjustColor(data.couleur_primaire, 20));
        }
      }
    } catch (error) {
      console.error('Erreur lors du chargement des paramètres:', error);
    }
  };

  // Fonction pour ajuster la luminosité d'une couleur
  const adjustColor = (color, amount) => {
    const usePound = color[0] === '#';
    const col = usePound ? color.slice(1) : color;
    const num = parseInt(col, 16);
    let r = (num >> 16) + amount;
    let g = (num >> 8 & 0x00FF) + amount;
    let b = (num & 0x0000FF) + amount;
    r = r > 255 ? 255 : r < 0 ? 0 : r;
    g = g > 255 ? 255 : g < 0 ? 0 : g;
    b = b > 255 ? 255 : b < 0 ? 0 : b;
    return (usePound ? '#' : '') + (r << 16 | g << 8 | b).toString(16).padStart(6, '0');
  };

  const checkAuth = async () => {
    try {
      const response = await fetch(`${API_URL}/api/auth/me`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const userData = await response.json();
        setCurrentUser(userData);
        setIsLoggedIn(true);
        setShowLoginForm(false);
      } else {
        localStorage.removeItem('token');
        setToken(null);
        setIsLoggedIn(false);
        setShowLoginForm(true);
      }
    } catch (error) {
      console.error('Erreur de vérification:', error);
      localStorage.removeItem('token');
      setToken(null);
      setIsLoggedIn(false);
      setShowLoginForm(true);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const email = formData.get('email');

    try {
      const response = await apiCall(`${API_URL}/api/auth/forgot-password`, {
        method: 'POST',
        body: JSON.stringify({ email: email })
      });

      if (response.ok) {
        const result = await response.json();
        showNotification('Email de réinitialisation envoyé si le compte existe', 'success');
        setShowForgotPasswordForm(false);
      } else {
        const errorData = await response.json();
        showNotification(errorData.detail || 'Erreur lors de la demande de réinitialisation', 'error');
      }
    } catch (error) {
      console.error('Erreur lors de la demande de réinitialisation:', error);
      showNotification('Erreur lors de la demande de réinitialisation', 'error');
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const token = formData.get('token');
    const newPassword = formData.get('new_password');
    const confirmPassword = formData.get('confirm_password');

    if (newPassword !== confirmPassword) {
      showNotification('Les mots de passe ne correspondent pas', 'error');
      return;
    }

    if (newPassword.length < 8) {
      showNotification('Le mot de passe doit contenir au moins 8 caractères', 'error');
      return;
    }

    try {
      const response = await apiCall(`${API_URL}/api/auth/reset-password`, {
        method: 'POST',
        body: JSON.stringify({ token: token, new_password: newPassword })
      });

      if (response.ok) {
        const result = await response.json();
        showNotification('Mot de passe réinitialisé avec succès', 'success');
        setShowResetPasswordForm(false);
        setShowLoginForm(true);
      } else {
        const errorData = await response.json();
        showNotification(errorData.detail || 'Erreur lors de la réinitialisation', 'error');
      }
    } catch (error) {
      console.error('Erreur lors de la réinitialisation:', error);
      showNotification('Erreur lors de la réinitialisation', 'error');
    }
  };

  const apiCall = async (url, options = {}) => {
    const headers = {
      'Content-Type': 'application/json',
      ...options.headers
    };
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    return fetch(url, {
      ...options,
      headers
    });
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const loginData = {
      username: formData.get('username'),
      password: formData.get('password')
    };

    try {
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(loginData)
      });

      if (response.ok) {
        const data = await response.json();
        setToken(data.access_token);
        setCurrentUser(data.user);
        localStorage.setItem('token', data.access_token);
        setIsLoggedIn(true);
        setShowLoginForm(false);
        setLoginError('');
      } else {
        const errorData = await response.json();
        setLoginError(errorData.detail || 'Erreur de connexion');
      }
    } catch (error) {
      console.error('Erreur lors de la connexion:', error);
      setLoginError('Erreur de connexion');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setIsLoggedIn(false);
    setCurrentUser(null);
    setShowLoginForm(true);
    setActiveTab('dashboard');
    setIsMobileMenuOpen(false);
  };

  // NOUVEAU: Gestion du scroll pour back-to-top
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      setShowBackToTop(scrollTop > 300);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // NOUVEAU: Fonction back-to-top
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  // NOUVEAU: Fermer les menus sur clic extérieur
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (mobileUserMenuOpen && !event.target.closest('.header-actions')) {
        setMobileUserMenuOpen(false);
      }
      // Fermer le menu mobile seulement si on clique en dehors de la navigation ET du bouton toggle
      if (isMobileMenuOpen && 
          !event.target.closest('.nav-container') && 
          !event.target.closest('.mobile-menu-toggle')) {
        setIsMobileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [mobileUserMenuOpen, isMobileMenuOpen]);

  const loadClients = async () => {
    try {
      const response = await apiCall(`${API_URL}/api/clients`);
      if (response.ok) {
        const data = await response.json();
        setClients(data);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des clients:', error);
    }
  };

  const loadProduits = async () => {
    try {
      const response = await apiCall(`${API_URL}/api/produits`);
      if (response.ok) {
        const data = await response.json();
        // Trier les produits par ordre alphabétique (nom)
        const produitsTriés = data.sort((a, b) => a.nom.localeCompare(b.nom));
        setProduits(produitsTriés);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des produits:', error);
    }
  };

  const loadCommandes = async () => {
    try {
      const response = await apiCall(`${API_URL}/api/commandes`);
      if (response.ok) {
        const data = await response.json();
        // Trier les commandes par date de création (plus récente en premier)
        const commandesTriees = data.sort((a, b) => {
          const dateA = new Date(a.date_commande || 0);
          const dateB = new Date(b.date_commande || 0);
          return dateB - dateA; // Ordre décroissant (plus récent en premier)
        });
        setCommandes(commandesTriees);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des commandes:', error);
    }
  };

  const loadUsers = async () => {
    try {
      const response = await apiCall(`${API_URL}/api/auth/users`);
      if (response.ok) {
        const data = await response.json();
        setUsers(data);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des utilisateurs:', error);
    }
  };

  const loadStatistiques = async () => {
    try {
      const response = await apiCall(`${API_URL}/api/statistiques`);
      if (response.ok) {
        const data = await response.json();
        setStatistiques(data);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des statistiques:', error);
    }
  };

  const loadStatistiquesProduits = async () => {
    try {
      const response = await apiCall(`${API_URL}/api/statistiques-produits`);
      if (response.ok) {
        const data = await response.json();
        setStatistiquesProduits(data);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des statistiques produits:', error);
    }
  };

  const handleClientSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const clientData = {
      nom_entreprise: formData.get('nom_entreprise'),
      siret: formData.get('siret'),
      adresse: formData.get('adresse'),
      telephone: formData.get('telephone'),
      email: formData.get('email'),
      type_client: formData.get('type_client')
    };

    try {
      const url = editingClient ? `${API_URL}/api/clients/${editingClient.id}` : `${API_URL}/api/clients`;
      const method = editingClient ? 'PUT' : 'POST';
      
      const response = await apiCall(url, {
        method: method,
        body: JSON.stringify(clientData)
      });

      if (response.ok) {
        loadClients();
        setShowClientForm(false);
        setEditingClient(null);
        showNotification(editingClient ? 'Client modifié avec succès' : 'Client créé avec succès', 'success');
      } else {
        showNotification('Erreur lors de la sauvegarde du client', 'error');
      }
    } catch (error) {
      console.error('Erreur lors de la sauvegarde du client:', error);
      showNotification('Erreur lors de la sauvegarde du client', 'error');
    }
  };

  const handleProduitSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    
    const produitData = {
      nom: formData.get('nom'),
      description: formData.get('description'),
      famille: formData.get('famille') || null,
      bio: formData.get('bio') === 'on', // checkbox value
      fin_de_saison: formData.get('fin_de_saison') === 'on', // checkbox value
      prix_unitaire: parseFloat(formData.get('prix_unitaire')),
      unite: formData.get('unite'),
      stock_actuel: parseFloat(formData.get('stock_actuel')),
      seuil_stock_min: parseFloat(formData.get('seuil_stock_min')) || null,
      prix_par_type: {
        prix_gms: parseFloat(formData.get('prix_gms')) || parseFloat(formData.get('prix_unitaire')),
        prix_boutique: parseFloat(formData.get('prix_boutique')) || parseFloat(formData.get('prix_unitaire')),
        prix_particulier: parseFloat(formData.get('prix_particulier')) || parseFloat(formData.get('prix_unitaire'))
      }
    };

    try {
      const url = editingProduit ? `${API_URL}/api/produits/${editingProduit.id}` : `${API_URL}/api/produits`;
      const method = editingProduit ? 'PUT' : 'POST';
      
      const response = await apiCall(url, {
        method: method,
        body: JSON.stringify(produitData)
      });

      if (response.ok) {
        loadProduits();
        setShowProduitForm(false);
        setEditingProduit(null);
        showNotification(editingProduit ? 'Produit modifié avec succès' : 'Produit créé avec succès', 'success');
      } else {
        showNotification('Erreur lors de la sauvegarde du produit', 'error');
      }
    } catch (error) {
      console.error('Erreur lors de la sauvegarde du produit:', error);
      showNotification('Erreur lors de la sauvegarde du produit', 'error');
    }
  };

  // Fonction pour sauvegarder un brouillon (crée une vraie commande avec statut brouillon)
  const saveBrouillon = async (e) => {
    e.preventDefault();
    // Récupérer le formulaire parent du bouton
    const form = e.target.closest('form');
    if (!form) {
      showNotification('Erreur: formulaire non trouvé', 'error');
      return;
    }
    const formData = new FormData(form);
    
    // Utiliser la même logique que handleCommandeSubmit mais avec statut brouillon
    const clientId = formData.get('client_id');
    const client = clients.find(c => c.id === clientId);
    
    if (!client) {
      showNotification('Veuillez sélectionner un client', 'error');
      return;
    }
    
    // Utiliser lignesProduits (React state) au lieu de FormData pour les produits
    const produitsCommande = [];
    for (const ligne of lignesProduits) {
      const produit = produits.find(p => p.id === ligne.produit_id);
      if (produit && ligne.quantite > 0) {
        let prixDefaut = produit.prix_unitaire;
        if (produit.prix_par_type && client && settings?.types_clients) {
          const typeIndex = settings.types_clients.findIndex(type => 
            type.toLowerCase() === client.type_client.toLowerCase()
          );
          
          switch (typeIndex) {
            case 0:
              prixDefaut = produit.prix_par_type.prix_gms || produit.prix_unitaire;
              break;
            case 1:
              prixDefaut = produit.prix_par_type.prix_boutique || produit.prix_unitaire;
              break;
            case 2:
              prixDefaut = produit.prix_par_type.prix_particulier || produit.prix_unitaire;
              break;
            default:
              prixDefaut = produit.prix_unitaire;
          }
        }
        
        const prixApplique = ligne.prix_personnalise && parseFloat(ligne.prix_personnalise) > 0 
          ? parseFloat(ligne.prix_personnalise) 
          : prixDefaut;
        
        produitsCommande.push({
          produit_id: produit.id,
          nom_produit: produit.nom,
          quantite: parseFloat(ligne.quantite),
          prix_unitaire: prixApplique,
          prix_defaut: prixDefaut,
          unite: produit.unite,
          bio: produit.bio || false
        });
      }
    }

    if (produitsCommande.length === 0) {
      showNotification('Veuillez ajouter au moins un produit', 'error');
      return;
    }

    const totalProduits = produitsCommande.reduce((sum, p) => sum + Math.round(p.quantite * p.prix_unitaire * 100), 0) / 100;
    const fraisLivraison = parseFloat(formData.get('frais_livraison')) || 0;

    const commandeData = {
      client_id: clientId,
      nom_client: client.nom_entreprise,
      produits: produitsCommande,
      total: totalProduits,
      frais_livraison: fraisLivraison,
      statut: 'brouillon' // STATUT BROUILLON
    };

    const dateCommande = formData.get('date_commande');
    if (dateCommande) {
      const date = new Date(dateCommande);
      commandeData.date_commande = date.toISOString();
    }

    try {
      const response = await apiCall(`${API_URL}/api/commandes`, {
        method: 'POST',
        body: JSON.stringify(commandeData)
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success !== false) {
          showNotification('Brouillon sauvegardé avec succès', 'success');
          setShowCommandeForm(false);
          setEditingCommande(null);
          await loadCommandes(); // Recharger pour voir le brouillon
        } else {
          showNotification(result.message || 'Erreur lors de la sauvegarde', 'error');
        }
      } else {
        showNotification('Erreur lors de la sauvegarde du brouillon', 'error');
      }
    } catch (error) {
      console.error('Erreur lors de la sauvegarde du brouillon:', error);
      showNotification('Erreur lors de la sauvegarde du brouillon', 'error');
    }
  };

  // Supprimer les anciennes fonctions de brouillon localStorage
  // const loadBrouillon, deleteBrouillon, etc. - on ne les utilise plus

  // Fonctions pour gérer les lignes de produits
  const ajouterLigneProduit = () => {
    setLignesProduits([...lignesProduits, { 
      id: Date.now(), 
      produit_id: '', 
      quantite: '', 
      prix_personnalise: '' 
    }]);
  };

  const supprimerLigneProduit = (index) => {
    if (lignesProduits.length > 1) {
      const nouvellesLignes = lignesProduits.filter((_, i) => i !== index);
      setLignesProduits(nouvellesLignes);
    }
  };

  const updateLigneProduit = (index, field, value) => {
    const nouvellesLignes = [...lignesProduits];
    nouvellesLignes[index][field] = value;
    setLignesProduits(nouvellesLignes);
  };

  // Réinitialiser les lignes quand on ouvre/ferme le formulaire
  const openCommandeForm = (commande = null) => {
    if (commande) {
      // Mode édition - charger les produits existants
      setEditingCommande(commande);
      const lignesExistantes = commande.produits.map(p => ({
        id: Date.now() + Math.random(),
        produit_id: p.produit_id,
        quantite: p.quantite,
        prix_personnalise: p.prix_unitaire !== p.prix_defaut ? p.prix_unitaire : ''
      }));
      setLignesProduits(lignesExistantes.length > 0 ? lignesExistantes : [
        { id: Date.now(), produit_id: '', quantite: '', prix_personnalise: '' }
      ]);
    } else {
      // Nouvelle commande
      setEditingCommande(null);
      setLignesProduits([{ id: Date.now(), produit_id: '', quantite: '', prix_personnalise: '' }]);
    }
    setShowCommandeForm(true);
  };

  const closeCommandeForm = () => {
    setShowCommandeForm(false);
    setEditingCommande(null);
    setLignesProduits([{ id: Date.now(), produit_id: '', quantite: '', prix_personnalise: '' }]);
  };

  // Fonction pour filtrer les commandes
  const getFilteredCommandes = () => {
    let filtered = [...commandes];
    
    // Filtre par client
    if (commandeClientFilter) {
      filtered = filtered.filter(commande => commande.client_id === commandeClientFilter);
    }
    
    // Filtre par recherche de numéros
    if (commandeSearchTerm) {
      const searchLower = commandeSearchTerm.toLowerCase();
      filtered = filtered.filter(commande => {
        const commandeId = (commande.id || '').toLowerCase();
        const numeroFacture = (commande.numero_facture || '').toLowerCase();
        const numeroAvoir = (commande.numero_avoir || '').toLowerCase();
        
        return commandeId.includes(searchLower) || 
               numeroFacture.includes(searchLower) || 
               numeroAvoir.includes(searchLower);
      });
    }
    
    return filtered;
  };

  // Fonction pour mapper les anciens types vers les nouveaux
  const getDisplayClientType = (clientType) => {
    if (!settings?.types_clients) {
      return clientType; // Pas de settings chargés, afficher le type tel quel
    }
    
    // Mapping des anciens types vers les nouveaux
    const typeMapping = {
      'particulier': settings.types_clients[2] || 'particulier', // 3ème type
      'boutique': settings.types_clients[1] || 'boutique',       // 2ème type  
      'GMS': settings.types_clients[0] || 'GMS'                  // 1er type
    };
    
    return typeMapping[clientType] || clientType;
  };

  // Charger les brouillons depuis la base de données
  const getBrouillons = () => {
    return commandes.filter(commande => commande.statut === 'brouillon');
  };

  const handleCommandeSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const clientId = formData.get('client_id');
    const client = clients.find(c => c.id === clientId);
    
    // Collecter les produits depuis lignesProduits (React state)
    const produitsCommande = [];
    
    for (let ligne of lignesProduits) {
      if (ligne.produit_id && ligne.quantite && parseFloat(ligne.quantite) > 0) {
        const produit = produits.find(p => p.id === ligne.produit_id);
        if (produit) {
          // Calculer le prix selon le type de client
          let prixDefaut = produit.prix_unitaire;
          if (produit.prix_par_type && client && settings?.types_clients) {
            const typeIndex = settings.types_clients.findIndex(type => 
              type.toLowerCase() === client.type_client.toLowerCase()
            );
            
            switch (typeIndex) {
              case 0: // Premier type (ex: GMS)
                prixDefaut = produit.prix_par_type.prix_gms || produit.prix_unitaire;
                break;
              case 1: // Deuxième type (ex: boutique)
                prixDefaut = produit.prix_par_type.prix_boutique || produit.prix_unitaire;
                break;
              case 2: // Troisième type (ex: particulier)
                prixDefaut = produit.prix_par_type.prix_particulier || produit.prix_unitaire;
                break;
              default:
                // Si le type n'est pas trouvé, utiliser le prix unitaire
                prixDefaut = produit.prix_unitaire;
            }
          }
          
          const prixApplique = ligne.prix_personnalise && parseFloat(ligne.prix_personnalise) > 0 
            ? parseFloat(ligne.prix_personnalise) 
            : prixDefaut;
          
          produitsCommande.push({
            produit_id: produit.id,
            nom_produit: produit.nom,
            quantite: parseFloat(ligne.quantite),
            prix_unitaire: prixApplique,
            prix_defaut: prixDefaut,
            unite: produit.unite,
            bio: produit.bio || false
          });
        }
      }
    }

    // Calculer le total des produits
    const totalProduits = produitsCommande.reduce((sum, p) => sum + Math.round(p.quantite * p.prix_unitaire * 100), 0) / 100;
    
    // Récupérer les frais de livraison
    const fraisLivraison = parseFloat(formData.get('frais_livraison')) || 0;
    
    // Total général = produits + frais de livraison
    const total = totalProduits + fraisLivraison;

    const commandeData = {
      client_id: clientId,
      nom_client: client.nom_entreprise,
      produits: produitsCommande,
      total: totalProduits, // Le total reste le total des produits seuls pour compatibilité
      frais_livraison: fraisLivraison,
      statut: (editingCommande?.statut === 'brouillon') ? 'en_cours' : (editingCommande ? editingCommande.statut : 'en_cours')
    };

    // Ajouter la date de commande si elle est spécifiée
    const dateCommande = formData.get('date_commande');
    if (dateCommande) {
      // Créer une date en UTC pour éviter les problèmes de fuseau horaire
      // dateCommande est au format YYYY-MM-DD (input date HTML)
      const [year, month, day] = dateCommande.split('-');
      const date = new Date(Date.UTC(parseInt(year), parseInt(month) - 1, parseInt(day)));
      commandeData.date_commande = date.toISOString();
    } else if (!editingCommande) {
      // Pour une nouvelle commande sans date, la date par défaut sera gérée par le backend
      // Pour une commande existante, on ne modifie pas la date si elle n'est pas fournie
    }

    try {
      const url = editingCommande ? `${API_URL}/api/commandes/${editingCommande.id}` : `${API_URL}/api/commandes`;
      const method = editingCommande ? 'PUT' : 'POST';
      
      const response = await apiCall(url, {
        method: method,
        body: JSON.stringify(commandeData)
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success !== false) {
          showNotification(editingCommande ? 'Commande mise à jour avec succès' : 'Commande créée avec succès', 'success');
          setShowCommandeForm(false);
          setEditingCommande(null);
          await refreshDashboard(); // Attendre le rafraîchissement complet
        } else {
          showNotification(result.message || 'Problème avec le stock', 'warning');
        }
      } else {
        showNotification('Erreur lors de la sauvegarde de la commande', 'error');
      }
    } catch (error) {
      console.error('Erreur lors de la sauvegarde de la commande:', error);
      showNotification('Erreur lors de la sauvegarde de la commande', 'error');
    }
  };

  const handleUserSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const userData = {
      username: formData.get('username'),
      email: formData.get('email'),
      password: formData.get('password'),
      role: formData.get('role')
    };

    try {
      const response = await apiCall(`${API_URL}/api/auth/register`, {
        method: 'POST',
        body: JSON.stringify(userData)
      });

      if (response.ok) {
        loadUsers();
        setShowUserForm(false);
        showNotification('Utilisateur créé avec succès', 'success');
      } else {
        const errorData = await response.json();
        showNotification(errorData.detail || 'Erreur lors de la création de l\'utilisateur', 'error');
      }
    } catch (error) {
      console.error('Erreur lors de la création de l\'utilisateur:', error);
      showNotification('Erreur lors de la création de l\'utilisateur', 'error');
    }
  };

  const handleSettingsSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    
    const settingsData = {
      nom_entreprise: formData.get('nom_entreprise'),
      adresse: formData.get('adresse'),
      siret: formData.get('siret'),
      numero_tva_intra: formData.get('numero_tva_intra'),
      numero_ecocert: formData.get('numero_ecocert'),
      en_conversion_bio: formData.get('en_conversion_bio') === 'on', // checkbox value
      numero_iban: formData.get('numero_iban'),
      capital_social: formData.get('capital_social'),
      couleur_primaire: formData.get('couleur_primaire'),
      logo_url: settings.logo_url || null, // CORRECTION: Préserver le logo existant
      seuil_stock_defaut: parseFloat(formData.get('seuil_stock_defaut')) || 10.0,
      gestion_stock_active: formData.get('gestion_stock_active') === 'on', // checkbox value
      types_clients: [
        formData.get('type_client_1'),
        formData.get('type_client_2'),
        formData.get('type_client_3')
      ].filter(Boolean)
    };

    try {
      const response = await apiCall(`${API_URL}/api/settings`, {
        method: 'PUT',
        body: JSON.stringify(settingsData)
      });

      if (response.ok) {
        await loadSettings(); // Recharger les settings
        await loadClients(); // Recharger les clients pour appliquer les nouveaux types
        await loadCommandes(); // Recharger aussi les commandes au cas où
        // Mettre à jour immédiatement l'état de gestion du stock
        setGestionStockActive(settingsData.gestion_stock_active);
        showNotification('Paramètres mis à jour avec succès', 'success');
      } else {
        showNotification('Erreur lors de la mise à jour des paramètres', 'error');
      }
    } catch (error) {
      console.error('Erreur lors de la sauvegarde des paramètres:', error);
      showNotification('Erreur lors de la sauvegarde des paramètres', 'error');
    }
  };

  // Fonction pour charger les alertes de stock
  const loadStockAlerts = async () => {
    try {
      const response = await fetch(`${API_URL}/api/stock/alerts`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setStockAlerts(data);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des alertes de stock:', error);
    }
  };

  // Fonction pour charger l'historique des mouvements
  const loadStockMovements = async (productId = null) => {
    try {
      const endpoint = productId ? `${API_URL}/api/stock/mouvements/${productId}` : `${API_URL}/api/stock/mouvements`;
      const response = await fetch(endpoint, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setStockMovements(data);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des mouvements de stock:', error);
    }
  };

  // Fonction pour ajouter du stock
  const handleStockSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const stockUpdates = [];
    
    // Récupérer tous les produits sélectionnés
    const productSelects = e.target.querySelectorAll('select[name="produit_id[]"]');
    const quantityInputs = e.target.querySelectorAll('input[name="quantite[]"]');
    
    for (let i = 0; i < productSelects.length; i++) {
      const productId = productSelects[i].value;
      const quantity = parseFloat(quantityInputs[i].value) || 0;
      
      if (productId && quantity > 0) {
        stockUpdates.push({
          produit_id: productId,
          quantite: quantity
        });
      }
    }
    
    if (stockUpdates.length === 0) {
      showNotification('Veuillez sélectionner au moins un produit et une quantité valide', 'error');
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/produits/stock`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(stockUpdates)
      });
      
      if (response.ok) {
        const data = await response.json();
        showNotification(`Stock mis à jour pour ${data.produits_mis_a_jour.length} produit(s)`, 'success');
        setShowStockForm(false);
        loadProduits();
        loadStockAlerts();
      } else {
        showNotification('Erreur lors de la mise à jour du stock', 'error');
      }
    } catch (error) {
      console.error('Erreur:', error);
      showNotification('Erreur lors de la mise à jour du stock', 'error');
    }
  };

  // Fonctions pour la gestion avancée des utilisateurs
  const resetUserPassword = async (userId, username) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir réinitialiser le mot de passe de ${username} ?`)) {
      return;
    }
    
    try {
      const response = await apiCall(`${API_URL}/api/auth/users/${userId}/reset-password`, {
        method: 'POST'
      });

      if (response.ok) {
        const result = await response.json();
        showNotification(`Mot de passe réinitialisé. Nouveau mot de passe : ${result.new_password}`, 'success');
      } else {
        showNotification('Erreur lors de la réinitialisation', 'error');
      }
    } catch (error) {
      console.error('Erreur:', error);
      showNotification('Erreur lors de la réinitialisation', 'error');
    }
  };

  const changeUserRole = async (userId, newRole) => {
    try {
      const response = await apiCall(`${API_URL}/api/auth/users/${userId}/role`, {
        method: 'PUT',
        body: JSON.stringify({ role: newRole })
      });

      if (response.ok) {
        showNotification('Rôle modifié avec succès', 'success');
        loadUsers(); // Recharger la liste
      } else {
        showNotification('Erreur lors de la modification du rôle', 'error');
      }
    } catch (error) {
      console.error('Erreur:', error);
      showNotification('Erreur lors de la modification du rôle', 'error');
    }
  };

  const toggleUserActive = async (userId, currentStatus) => {
    const action = currentStatus ? 'désactiver' : 'activer';
    
    try {
      const response = await apiCall(`${API_URL}/api/auth/users/${userId}/toggle`, {
        method: 'PUT',
        body: JSON.stringify({ active: !currentStatus })
      });

      if (response.ok) {
        showNotification(`Utilisateur ${action} avec succès`, 'success');
        loadUsers(); // Recharger la liste
      } else {
        showNotification(`Erreur lors de la ${action}ation`, 'error');
      }
    } catch (error) {
      console.error('Erreur:', error);
      showNotification(`Erreur lors de la ${action}ation`, 'error');
    }
  };

  const deleteUser = async (userId) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cet utilisateur ?')) {
      try {
        const response = await fetch(`${API_URL}/api/auth/users/${userId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (response.ok) {
          showNotification('Utilisateur supprimé avec succès', 'success');
          loadUsers();
        } else {
          const errorData = await response.json();
          showNotification(errorData.detail || 'Erreur lors de la suppression', 'error');
        }
      } catch (error) {
        console.error('Erreur:', error);
        showNotification('Erreur lors de la suppression de l\'utilisateur', 'error');
      }
    }
  };

  // Fonction pour gérer l'upload de logo
  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${API_URL}/api/upload-logo`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      
      if (response.ok) {
        const data = await response.json();
        showNotification('Logo uploadé avec succès', 'success');
        loadSettings();
      } else {
        const errorData = await response.json();
        showNotification(errorData.detail || 'Erreur lors de l\'upload du logo', 'error');
      }
    } catch (error) {
      console.error('Erreur:', error);
      showNotification('Erreur lors de l\'upload du logo', 'error');
    }
  };

  // Fonction pour calculer le CA d'un client
  const getClientCA = (clientId) => {
    return commandes
      .filter(commande => commande.client_id === clientId)
      .reduce((total, commande) => total + (commande.total || 0), 0);
  };

  // Fonctions de recherche
  const getFilteredClients = () => {
    let filtered = clients;

    // Filtre par type de client
    if (clientTypeFilter) {
      filtered = filtered.filter(client => client.type_client === clientTypeFilter);
    }

    // Filtre par CA
    if (clientCaFilter === 'high') {
      filtered = filtered.filter(client => getClientCA(client.id) > 1000);
    } else if (clientCaFilter === 'low') {
      filtered = filtered.filter(client => getClientCA(client.id) <= 1000);
    }

    // Recherche par mot-clé (nom, SIRET, email, téléphone)
    if (searchTermClients) {
      const searchTerm = searchTermClients.toLowerCase();
      filtered = filtered.filter(client =>
        (client.nom_entreprise || '').toLowerCase().includes(searchTerm) ||
        (client.siret || '').toLowerCase().includes(searchTerm) ||
        (client.email || '').toLowerCase().includes(searchTerm) ||
        (client.telephone || '').toLowerCase().includes(searchTerm) ||
        (client.adresse || '').toLowerCase().includes(searchTerm)
      );
    }

    return filtered;
  };

  const getFilteredProduits = () => {
    let filtered = produits;

    // Filtre par famille
    if (produitFamilleFilter) {
      filtered = filtered.filter(produit => produit.famille === produitFamilleFilter);
    }

    // Recherche par mot-clé (nom, description, famille)
    if (searchTermProduits) {
      const searchTerm = searchTermProduits.toLowerCase();
      filtered = filtered.filter(produit =>
        (produit.nom || '').toLowerCase().includes(searchTerm) ||
        (produit.description || '').toLowerCase().includes(searchTerm) ||
        (produit.famille || '').toLowerCase().includes(searchTerm)
      );
    }

    // Trier pour mettre les produits fin de saison en dernier
    filtered = filtered.sort((a, b) => {
      // Si un des produits est fin de saison et pas l'autre, mettre le fin de saison à la fin
      if (a.fin_de_saison && !b.fin_de_saison) return 1;
      if (!a.fin_de_saison && b.fin_de_saison) return -1;
      // Sinon, trier par nom alphabétiquement
      return (a.nom || '').localeCompare(b.nom || '');
    });

    return filtered;
  };

  // Fonction pour déterminer si une commande peut être supprimée
  const canDeleteCommande = (commande) => {
    // Une commande ne peut pas être supprimée si elle a déjà été facturée
    if (commande.numero_facture || commande.statut === 'facture') {
      return false;
    }
    
    // Trier les commandes par date pour trouver la plus récente
    const commandesTriees = [...commandes].sort((a, b) => {
      const getDateForSorting = (cmd) => {
        const date = cmd.date_commande;
        if (!date) return new Date(0); // Date très ancienne si null
        try {
          return new Date(date);
        } catch {
          return new Date(0);
        }
      };
      
      return getDateForSorting(b) - getDateForSorting(a); // Plus récent en premier
    });
    
    // Vérifier si c'est la dernière commande (la plus récente)
    if (commandesTriees.length > 0) {
      const derniereCommande = commandesTriees[0];
      return commande.id === derniereCommande.id;
    }
    
    return true; // Si c'est la seule commande, elle peut être supprimée
  };
  const getPaginatedItems = (items, currentPage, itemsPerPage) => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return items.slice(startIndex, endIndex);
  };

  const getTotalPages = (items, itemsPerPage) => {
    return Math.ceil(items.length / itemsPerPage);
  };

  // Composant de pagination réutilisable
  const Pagination = ({ items, currentPage, setCurrentPage, itemsPerPage, setItemsPerPage }) => {
    const totalPages = getTotalPages(items, itemsPerPage);

    return (
      <div className="pagination-container">
        <div className="pagination-info">
          <span>
            Affichage {Math.min((currentPage - 1) * itemsPerPage + 1, items.length)} à{' '}
            {Math.min(currentPage * itemsPerPage, items.length)} sur {items.length} éléments
          </span>
        </div>
        
        <div className="pagination-controls">
          <div className="items-per-page">
            <label>Éléments par page:</label>
            <select 
              value={itemsPerPage} 
              onChange={(e) => {
                const newItemsPerPage = parseInt(e.target.value);
                setItemsPerPage(newItemsPerPage);
                setCurrentPage(1); // Reset à la première page
              }}
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>

          <div className="page-navigation">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="btn btn-secondary btn-sm"
            >
              ← Précédent
            </button>
            
            <span className="page-info">
              Page {currentPage} sur {totalPages}
            </span>
            
            <button
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              className="btn btn-secondary btn-sm"
            >
              Suivant →
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Fonction pour filtrer les clients
  const filterClients = () => {
    let filtered = [...clients];
    
    // Ajouter le CA à chaque client
    filtered = filtered.map(client => ({
      ...client,
      ca_total: getClientCA(client.id)
    }));
    
    // Filtrer par type
    if (clientTypeFilter) {
      filtered = filtered.filter(client => 
        client.type_client.toLowerCase() === clientTypeFilter.toLowerCase()
      );
    }
    
    // Filtrer par CA
    if (clientCaFilter) {
      const caValue = parseFloat(clientCaFilter);
      filtered = filtered.filter(client => client.ca_total >= caValue);
    }
    
    setFilteredClients(filtered);
  };

  // Fonction pour filtrer les produits
  const filterProduits = () => {
    let filtered = [...produits];
    
    // Filtrer par famille
    if (produitFamilleFilter) {
      filtered = filtered.filter(produit => 
        produit.famille && produit.famille.toLowerCase() === produitFamilleFilter.toLowerCase()
      );
    }
    
    setFilteredProduits(filtered);
  };

  // Effet pour filtrer les produits quand les filtres changent
  useEffect(() => {
    if (produits.length > 0) {
      filterProduits();
    }
  }, [produits, produitFamilleFilter]);

  // Effet pour filtrer les clients quand les filtres changent
  useEffect(() => {
    if (clients.length > 0 && commandes.length >= 0) {
      filterClients();
    }
  }, [clients, commandes, clientTypeFilter, clientCaFilter]);

  // Fonction pour mettre à jour le prix par défaut selon le type de client
  const updatePrixDefaut = (e) => {
    const select = e.target;
    const option = select.selectedOptions[0];
    if (!option) return;

    const prixDefaut = parseFloat(option.dataset.prixDefaut) || 0;
    const prixGms = parseFloat(option.dataset.prixGms) || prixDefaut;
    const prixBoutique = parseFloat(option.dataset.prixBoutique) || prixDefaut;
    const prixParticulier = parseFloat(option.dataset.prixParticulier) || prixDefaut;

    // Récupérer le type de client sélectionné
    const clientSelect = document.getElementById('client-select');
    const clientId = clientSelect?.value;
    const client = clients.find(c => c.id === clientId);
    
    let prixApplique = prixDefaut;
    if (client) {
      switch (client.type_client.toLowerCase()) {
        case 'gms':
          prixApplique = prixGms;
          break;
        case 'boutique':
          prixApplique = prixBoutique;
          break;
        case 'particulier':
          prixApplique = prixParticulier;
          break;
        default:
          prixApplique = prixDefaut;
      }
    }

    // Mettre à jour l'affichage du prix par défaut
    const ligne = select.closest('.produit-ligne');
    const prixInfo = ligne?.querySelector('.prix-defaut');
    if (prixInfo) {
      prixInfo.textContent = `Prix par défaut: ${prixApplique.toFixed(2)} €`;
    }
  };

  const updateCommandeStatut = async (commandeId, nouveauStatut) => {
    // Trouver la commande
    const commande = commandes.find(c => c.id === commandeId);
    if (!commande) {
      showNotification('Commande non trouvée', 'error');
      return;
    }

    // Vérifier les restrictions de changement de statut
    if (commande.statut === 'facture') {
      // Une commande facturée peut passer en statut annulé (pour les remboursements)
      if (nouveauStatut !== 'facture' && nouveauStatut !== 'annule') {
        showNotification('Une commande facturée ne peut passer qu\'en statut annulé', 'error');
        return;
      }
    }

    try {
      const response = await apiCall(`${API_URL}/api/commandes/${commandeId}/statut`, {
        method: 'PUT',
        body: JSON.stringify({ statut: nouveauStatut })
      });

      if (response.ok) {
        await refreshDashboard(); // Attendre le rafraîchissement complet
        showNotification('Statut mis à jour avec succès', 'success');
      } else {
        showNotification('Erreur lors de la mise à jour du statut', 'error');
      }
    } catch (error) {
      console.error('Erreur lors de la mise à jour du statut:', error);
      showNotification('Erreur lors de la mise à jour du statut', 'error');
    }
  };

  const deleteClient = async (clientId) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce client ?')) {
      try {
        const response = await apiCall(`${API_URL}/api/clients/${clientId}`, {
          method: 'DELETE'
        });
        if (response.ok) {
          loadClients();
          showNotification('Client supprimé avec succès', 'success');
        } else {
          showNotification('Erreur lors de la suppression du client', 'error');
        }
      } catch (error) {
        console.error('Erreur lors de la suppression du client:', error);
        showNotification('Erreur lors de la suppression du client', 'error');
      }
    }
  };

  const deleteProduit = async (produitId) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce produit ?')) {
      try {
        const response = await apiCall(`${API_URL}/api/produits/${produitId}`, {
          method: 'DELETE'
        });
        if (response.ok) {
          loadProduits();
          showNotification('Produit supprimé avec succès', 'success');
        } else {
          showNotification('Erreur lors de la suppression du produit', 'error');
        }
      } catch (error) {
        console.error('Erreur lors de la suppression du produit:', error);
        showNotification('Erreur lors de la suppression du produit', 'error');
      }
    }
  };

  const deleteCommande = async (commandeId) => {
    // Trouver la commande à supprimer
    const commande = commandes.find(c => c.id === commandeId);
    if (!commande) {
      showNotification('Commande non trouvée', 'error');
      return;
    }

    // Vérifier si c'est la dernière commande (pour préserver la numérotation des factures)
    const commandesTriees = [...commandes].sort((a, b) => {
      const dateA = new Date(a.date_commande || 0);
      const dateB = new Date(b.date_commande || 0);
      return dateB - dateA; // Plus récent en premier
    });
    
    if (commandesTriees.length > 1 && commande.id !== commandesTriees[0].id) {
      showNotification('Seule la dernière commande peut être supprimée pour préserver la numérotation', 'warning');
      return;
    }

    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette commande ?')) {
      try {
        const response = await apiCall(`${API_URL}/api/commandes/${commandeId}`, {
          method: 'DELETE'
        });
        if (response.ok) {
          loadCommandes();
          showNotification('Commande supprimée avec succès', 'success');
        } else {
          showNotification('Erreur lors de la suppression de la commande', 'error');
        }
      } catch (error) {
        console.error('Erreur lors de la suppression de la commande:', error);
        showNotification('Erreur lors de la suppression de la commande', 'error');
      }
    }
  };

  // États pour les modals PDF mobile
  const [showMobilePDF, setShowMobilePDF] = useState(false);
  const [mobilePDFContent, setMobilePDFContent] = useState('');
  const [mobilePDFTitle, setMobilePDFTitle] = useState('');

  // NOUVEAU: Fonction d'impression pour mobile PDF
  const printMobilePDF = () => {
    // Créer une nouvelle fenêtre pour l'impression
    const printWindow = window.open('', '_blank');
    
    // Injecter le contenu PDF dans la nouvelle fenêtre
    printWindow.document.write(mobilePDFContent);
    printWindow.document.close();
    
    // Attendre que le contenu soit chargé puis imprimer
    printWindow.onload = () => {
      printWindow.print();
      printWindow.close();
    };
  };

  // NOUVEAU: Détection mobile/tablet
  const isMobileDevice = () => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
           window.innerWidth <= 1024;
  };

  const genererBonLivraison = async (commandeId) => {
    try {
      const response = await apiCall(`${API_URL}/api/bon-livraison/${commandeId}`, {
        method: 'GET'
      });

      if (response.ok) {
        const html = await response.text();
        
        if (isMobileDevice()) {
          // Sur mobile/iPad: afficher dans un modal plein écran
          setMobilePDFContent(html);
          setMobilePDFTitle('Bon de Livraison');
          setShowMobilePDF(true);
        } else {
          // Sur desktop: comportement normal popup
          const newWindow = window.open('', '_blank');
          newWindow.document.write(html);
          newWindow.document.close();
        }
        
        showNotification('Bon de livraison généré avec succès', 'success');
      } else {
        showNotification('Erreur lors de la génération du bon de livraison', 'error');
      }
    } catch (error) {
      console.error('Erreur lors de la génération du bon de livraison:', error);
      showNotification('Erreur lors de la génération du bon de livraison', 'error');
    }
  };

  const genererFacture = async (commandeId) => {
    try {
      const response = await apiCall(`${API_URL}/api/facture/${commandeId}`, {
        method: 'GET'
      });

      if (response.ok) {
        const html = await response.text();
        
        if (isMobileDevice()) {
          // Sur mobile/iPad: afficher dans un modal plein écran
          setMobilePDFContent(html);
          setMobilePDFTitle('Facture');
          setShowMobilePDF(true);
        } else {
          // Sur desktop: comportement normal popup
          const newWindow = window.open('', '_blank');
          newWindow.document.write(html);
          newWindow.document.close();
        }
        
        showNotification('Facture générée avec succès', 'success');
        refreshDashboard(); // Rafraîchir pour mettre à jour le statut
      } else {
        const errorData = await response.json();
        showNotification(errorData.detail || 'Erreur lors de la génération de la facture', 'error');
      }
    } catch (error) {
      console.error('Erreur lors de la génération de la facture:', error);
      showNotification('Erreur lors de la génération de la facture', 'error');
    }
  };

  const genererAvoir = async (commandeId) => {
    try {
      const response = await apiCall(`${API_URL}/api/avoir/${commandeId}`, {
        method: 'GET'
      });

      if (response.ok) {
        const html = await response.text();
        
        if (isMobileDevice()) {
          // Sur mobile/iPad: afficher dans un modal plein écran
          setMobilePDFContent(html);
          setMobilePDFTitle('Avoir');
          setShowMobilePDF(true);
        } else {
          // Sur desktop: comportement normal popup
          const newWindow = window.open('', '_blank');
          newWindow.document.write(html);
          newWindow.document.close();
        }
        
        showNotification('Avoir généré avec succès', 'success');
      } else {
        const errorData = await response.json();
        showNotification(errorData.detail || 'Erreur lors de la génération de l\'avoir', 'error');
      }
    } catch (error) {
      console.error('Erreur lors de la génération de l\'avoir:', error);
      showNotification('Erreur lors de la génération de l\'avoir', 'error');
    }
  };

  const getPrixPourClient = (produit, typeClient) => {
    if (!produit.prix_par_type) return produit.prix_unitaire;
    
    switch (typeClient) {
      case 'GMS':
        return produit.prix_par_type.prix_gms || produit.prix_unitaire;
      case 'boutique':
        return produit.prix_par_type.prix_boutique || produit.prix_unitaire;
      case 'particulier':
        return produit.prix_par_type.prix_particulier || produit.prix_unitaire;
      default:
        return produit.prix_unitaire;
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setIsMobileMenuOpen(false);
    if (tab === 'statistiques') {
      loadStatistiques();
    }
    if (tab === 'reglages') {
      loadSettings();
    }
  };

  // Écran de connexion
  if (showLoginForm) {
    return (
      <div className="login-container">
        <div className="login-card">
          <div className="login-header">
            <h1>🌱 {settings?.nom_entreprise || 'MaraichPro'}</h1>
            <p className="subtitle">Gestion Maraîcher Professionnelle</p>
          </div>
          
          {!showForgotPasswordForm && !showResetPasswordForm ? (
            // Formulaire de connexion
            <form onSubmit={handleLogin} className="login-form">
              <div className="form-group">
                <label>Nom d'utilisateur</label>
                <input
                  type="text"
                  name="username"
                  required
                  placeholder="Entrez votre nom d'utilisateur"
                />
              </div>
              <div className="form-group">
                <label>Mot de passe</label>
                <input
                  type="password"
                  name="password"
                  required
                  placeholder="Entrez votre mot de passe"
                />
              </div>
              {loginError && <div className="error-message">{loginError}</div>}
              <button type="submit" className="btn btn-primary btn-login">
                Se connecter
              </button>
              <div className="login-links">
                <button 
                  type="button" 
                  className="forgot-password-link"
                  onClick={() => setShowForgotPasswordForm(true)}
                >
                  Mot de passe oublié ?
                </button>
                <span className="link-separator"> | </span>
                <button 
                  type="button" 
                  className="forgot-password-link"
                  onClick={() => setShowResetPasswordForm(true)}
                >
                  J'ai un token de réinitialisation
                </button>
              </div>
            </form>
          ) : showForgotPasswordForm ? (
            // Formulaire mot de passe oublié
            <form onSubmit={handleForgotPassword} className="login-form">
              <div className="forgot-password-header">
                <h3>Réinitialiser le mot de passe</h3>
                <p>Entrez votre adresse email pour recevoir un token de réinitialisation</p>
              </div>
              <div className="form-group">
                <label>Adresse email</label>
                <input
                  type="email"
                  name="email"
                  required
                  placeholder="Entrez votre adresse email"
                />
              </div>
              <div className="form-actions-row">
                <button 
                  type="button" 
                  className="btn btn-secondary"
                  onClick={() => setShowForgotPasswordForm(false)}
                >
                  Retour
                </button>
                <button type="submit" className="btn btn-primary">
                  Envoyer le token
                </button>
              </div>
            </form>
          ) : (
            // Formulaire de réinitialisation avec token
            <form onSubmit={handleResetPassword} className="login-form">
              <div className="forgot-password-header">
                <h3>Nouveau mot de passe</h3>
                <p>Entrez votre token de réinitialisation et votre nouveau mot de passe</p>
              </div>
              <div className="form-group">
                <label>Token de réinitialisation</label>
                <input
                  type="text"
                  name="token"
                  required
                  placeholder="Collez votre token ici"
                  value={resetToken}
                  onChange={(e) => setResetToken(e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>Nouveau mot de passe</label>
                <input
                  type="password"
                  name="new_password"
                  required
                  placeholder="Au moins 8 caractères"
                  minLength="8"
                />
              </div>
              <div className="form-group">
                <label>Confirmer le mot de passe</label>
                <input
                  type="password"
                  name="confirm_password"
                  required
                  placeholder="Confirmez votre nouveau mot de passe"
                  minLength="8"
                />
              </div>
              <div className="form-actions-row">
                <button 
                  type="button" 
                  className="btn btn-secondary"
                  onClick={() => setShowResetPasswordForm(false)}
                >
                  Retour
                </button>
                <button type="submit" className="btn btn-primary">
                  Réinitialiser
                </button>
              </div>
            </form>
          )}
          
          <div className="login-footer">
            <div className="branding">
              <p>Made by <a href="https://www.maraichpro.com" target="_blank" rel="noopener noreferrer"><strong>Maraich'Pro</strong></a></p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Composant de notification toast
  const NotificationToast = ({ notification, onRemove }) => (
    <div className={`notification-toast ${notification.type}`}>
      <div className="notification-content">
        <div className="notification-icon">
          {notification.type === 'success' && '✅'}
          {notification.type === 'error' && '❌'}
          {notification.type === 'warning' && '⚠️'}
          {notification.type === 'info' && 'ℹ️'}
        </div>
        <div className="notification-message">{notification.message}</div>
        <button 
          className="notification-close"
          onClick={() => onRemove(notification.id)}
        >
          ×
        </button>
      </div>
    </div>
  );

  const renderDashboard = () => (
    <div className="dashboard">
      <div className="section-header">
        <h2>Tableau de bord</h2>
        <div className="user-info">
          <span>Connecté en tant que: <strong>{currentUser?.username}</strong> ({currentUser?.role})</span>
        </div>
      </div>
      
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon">👥</div>
          <div className="stat-content">
            <h3>Clients</h3>
            <p className="stat-number">{clients.length}</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">🥕</div>
          <div className="stat-content">
            <h3>Produits</h3>
            <p className="stat-number">{produits.length}</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">📋</div>
          <div className="stat-content">
            <h3>Commandes</h3>
            <p className="stat-number">{commandes.length}</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">💶</div>
          <div className="stat-content">
            <h3>CA du jour</h3>
            <p className="stat-number">
              {commandes
                .filter(c => c.date_commande && new Date(c.date_commande).toDateString() === new Date().toDateString())
                .reduce((sum, c) => sum + c.total, 0)
                .toFixed(2)} €
            </p>
          </div>
        </div>
      </div>
      
      <div className="recent-orders-section">
        <div className="section-header-mini">
          <h3>Commandes récentes</h3>
          <div className="orders-count">
            {commandes.slice(0, 5).length} dernières commandes
          </div>
        </div>
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Client</th>
                <th>Date</th>
                <th>Total</th>
                <th>Statut</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {commandes.slice(0, 5).map(commande => (
                <tr key={commande.id}>
                  <td>{commande.nom_client}</td>
                  <td>{commande.date_commande ? new Date(commande.date_commande).toLocaleDateString('fr-FR') : 'N/A'}</td>
                  <td>{commande.total.toFixed(2)} €</td>
                  <td>
                    <span className={`status ${commande.statut}`}>
                      {commande.statut === 'en_cours' ? 'En cours' : 
                       commande.statut === 'livre' ? 'Livrée' : 
                       commande.statut === 'facture' ? 'Facturée' : 
                       commande.statut === 'annule' ? 'Annulée' : 'En cours'}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button 
                        onClick={() => genererBonLivraison(commande.id)}
                        className="btn btn-primary btn-sm"
                        title="Générer bon de livraison"
                      >
                        Bon
                      </button>
                      {commande.statut === 'livre' && (
                        <button 
                          onClick={() => genererFacture(commande.id)}
                          className="btn btn-success btn-sm"
                          title="Générer facture"
                          style={{marginLeft: '5px'}}
                        >
                          Facture
                        </button>
                      )}
                      {(commande.statut === 'facture' || (commande.statut === 'annule' && commande.numero_facture)) && (
                        <button 
                          onClick={() => genererFacture(commande.id)}
                          className="btn btn-secondary btn-sm"
                          title={commande.numero_facture ? `Facture ${commande.numero_facture}` : "Télécharger facture"}
                          style={{marginLeft: '5px'}}
                        >
                          {commande.numero_facture || 'Facture'}
                        </button>
                      )}
                      {commande.statut === 'annule' && (
                        <button 
                          onClick={() => genererAvoir(commande.id)}
                          className="btn btn-danger btn-sm"
                          title="Générer avoir"
                          style={{marginLeft: '5px'}}
                        >
                          Avoir
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const renderClients = () => (
    <div className="clients">
      <div className="section-header">
        <h2>Gestion des Clients</h2>
        <button 
          onClick={() => setShowClientForm(true)}
          className="btn btn-primary"
        >
          Nouveau Client
        </button>
      </div>

      {/* Filtres clients */}
      <div className="filters-section">
        <div className="filters-row">
          <div className="filter-group search-group">
            <label>🔍 Rechercher</label>
            <input 
              type="text"
              placeholder="Nom, SIRET, email, téléphone, adresse..."
              value={searchTermClients}
              onChange={(e) => setSearchTermClients(e.target.value)}
              className="search-input"
            />
          </div>
          <div className="filter-group">
            <label>Filtrer par type</label>
            <select 
              value={clientTypeFilter} 
              onChange={(e) => setClientTypeFilter(e.target.value)}
              className="filter-select"
            >
              <option value="">Tous les types</option>
              <option value="GMS">GMS</option>
              <option value="boutique">Boutique</option>
              <option value="particulier">Particulier</option>
            </select>
          </div>
          <div className="filter-group">
            <label>CA minimum (€)</label>
            <input 
              type="number" 
              value={clientCaFilter} 
              onChange={(e) => setClientCaFilter(e.target.value)}
              placeholder="0"
              min="0"
              step="0.01"
              className="filter-input"
            />
          </div>
          <div className="filter-group">
            <button 
              onClick={() => {
                setSearchTermClients('');
                setClientTypeFilter('');
                setClientCaFilter('');
                setCurrentPageClients(1); // Retour à la première page
              }}
              className="btn btn-secondary btn-sm"
            >
              🔄 Réinitialiser
            </button>
          </div>
        </div>
      </div>

      {showClientForm && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>{editingClient ? 'Modifier le client' : 'Nouveau client'}</h3>
            <form onSubmit={handleClientSubmit}>
              <div className="form-group">
                <label>Nom de l'entreprise *</label>
                <input 
                  type="text" 
                  name="nom_entreprise" 
                  required 
                  defaultValue={editingClient?.nom_entreprise || ''}
                />
              </div>
              <div className="form-group">
                <label>SIRET *</label>
                <input 
                  type="text" 
                  name="siret" 
                  required 
                  defaultValue={editingClient?.siret || ''}
                />
              </div>
              <div className="form-group">
                <label>Adresse *</label>
                <textarea 
                  name="adresse" 
                  required 
                  defaultValue={editingClient?.adresse || ''}
                />
              </div>
              <div className="form-group">
                <label>Téléphone *</label>
                <input 
                  type="tel" 
                  name="telephone" 
                  required 
                  defaultValue={editingClient?.telephone || ''}
                />
              </div>
              <div className="form-group">
                <label>Email *</label>
                <input 
                  type="email" 
                  name="email" 
                  required 
                  defaultValue={editingClient?.email || ''}
                />
              </div>
              <div className="form-group">
                <label>Type de client *</label>
                <select 
                  name="type_client" 
                  required 
                  defaultValue={editingClient?.type_client || ''}
                >
                  <option value="">Sélectionnez un type</option>
                  {settings?.types_clients?.map(type => (
                    <option key={type} value={type}>{type}</option>
                  )) || (
                    <>
                      <option value="GMS">GMS</option>
                      <option value="boutique">Boutique</option>
                      <option value="particulier">Particulier</option>
                    </>
                  )}
                </select>
              </div>
              <div className="form-actions">
                <button type="submit" className="btn btn-primary">
                  {editingClient ? 'Modifier' : 'Créer'}
                </button>
                <button 
                  type="button" 
                  onClick={() => {
                    setShowClientForm(false);
                    setEditingClient(null);
                  }}
                  className="btn btn-secondary"
                >
                  Annuler
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>Entreprise</th>
              <th>SIRET</th>
              <th>Type</th>
              <th>Téléphone</th>
              <th>Email</th>
              <th>CA Total</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {getPaginatedItems(
              getFilteredClients(), 
              currentPageClients, 
              itemsPerPage
            ).map(client => (
              <tr key={client.id}>
                <td>{client.nom_entreprise}</td>
                <td>{client.siret}</td>
                <td>
                  <span className={`type-badge ${client.type_client}`}>
                    {getDisplayClientType(client.type_client)}
                  </span>
                </td>
                <td>{client.telephone}</td>
                <td>{client.email}</td>
                <td>
                  <strong>{(client.ca_total || getClientCA(client.id)).toFixed(2)} €</strong>
                </td>
                <td>
                  <div className="action-buttons">
                    <button 
                      onClick={() => {
                        setEditingClient(client);
                        setShowClientForm(true);
                      }}
                      className="btn-action btn-edit"
                      title="Modifier le client"
                    >
                      Modifier
                    </button>
                    <button 
                      onClick={() => deleteClient(client.id)}
                      className="btn-action btn-delete"
                      title="Supprimer le client"
                    >
                      Supprimer
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {getFilteredClients().length === 0 && (
              <tr>
                <td colSpan="7" style={{textAlign: 'center', padding: '2rem', color: '#6c757d'}}>
                  {clientTypeFilter || clientCaFilter || searchTermClients ? 
                    'Aucun client trouvé avec ces filtres' : 
                    'Aucun client enregistré'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      
      {/* Pagination des clients */}
      <Pagination 
        items={getFilteredClients()}
        currentPage={currentPageClients}
        setCurrentPage={setCurrentPageClients}
        itemsPerPage={itemsPerPage}
        setItemsPerPage={setItemsPerPage}
      />
    </div>
  );

  const renderProduits = () => (
    <div className="produits">
      <div className="section-header">
        <h2>Gestion des Produits</h2>
        <div className="header-buttons">
          {gestionStockActive && (
            <button 
              onClick={() => setShowStockForm(true)}
              className="btn btn-secondary"
            >
              📦 Ajouter Stock
            </button>
          )}
          <button 
            onClick={() => {
              setShowStockHistory(true);
              loadStockMovements();
            }}
            className="btn btn-secondary"
          >
            📈 Historique Stock
          </button>
          <button 
            onClick={() => setShowProduitForm(true)}
            className="btn btn-primary"
          >
            Nouveau Produit
          </button>
        </div>
      </div>

      {/* Alertes de stock - Masquées si gestion stock désactivée */}
      {gestionStockActive && stockAlerts.length > 0 && (
        <div className="stock-alerts">
          <h3>⚠️ Alertes de Stock</h3>
          <div className="alerts-container">
            {stockAlerts.map(alert => (
              <div key={alert.produit_id} className={`alert ${alert.niveau_alerte}`}>
                <span className="alert-icon">
                  {alert.niveau_alerte === 'critique' ? '🚨' : '⚠️'}
                </span>
                <span className="alert-text">
                  {alert.nom_produit}: {formatNumber(alert.stock_actuel)} {alert.unite} 
                  (seuil: {formatNumber(alert.seuil_min)} {alert.unite})
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Formulaire d'ajout de stock */}
      {showStockForm && (
        <div className="modal-overlay">
          <div className="modal modal-large">
            <div className="modal-header">
              <h3>📦 Ajouter du Stock</h3>
              <button className="modal-close" onClick={() => setShowStockForm(false)}>×</button>
            </div>
            <form onSubmit={handleStockSubmit}>
              <div className="stock-form-container">
                <div className="produit-stock-ligne">
                  <div className="form-group">
                    <label>Produit</label>
                    <select name="produit_id[]" required>
                      <option value="">Sélectionnez un produit</option>
                      {produits.filter(produit => !produit.fin_de_saison).map(produit => (
                        <option key={produit.id} value={produit.id}>
                          {produit.nom} - Stock actuel: {parseFloat(produit.stock_actuel).toFixed(2).replace(/\.?0+$/, '')} {produit.unite}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Quantité à ajouter</label>
                    <input type="number" name="quantite[]" step="0.01" min="0" required />
                  </div>
                </div>
              </div>
              
              <button type="button" className="btn btn-secondary btn-sm" onClick={() => {
                const container = document.querySelector('.stock-form-container');
                const newLigne = container.querySelector('.produit-stock-ligne').cloneNode(true);
                newLigne.querySelectorAll('input, select').forEach(input => input.value = '');
                container.appendChild(newLigne);
              }}>
                ➕ Ajouter un autre produit
              </button>
              
              <div className="form-actions">
                <button type="submit" className="btn btn-primary">
                  📦 Ajouter le Stock
                </button>
                <button 
                  type="button" 
                  onClick={() => setShowStockForm(false)}
                  className="btn btn-secondary"
                >
                  Annuler
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Historique des mouvements de stock */}
      {showStockHistory && (
        <div className="modal-overlay">
          <div className="modal modal-large">
            <div className="modal-header">
              <h3>📈 Historique des Mouvements de Stock</h3>
              <button className="modal-close" onClick={() => setShowStockHistory(false)}>×</button>
            </div>
            <div className="stock-history-container">
              {stockMovements.length > 0 ? (
                <table className="stock-history-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Produit</th>
                      <th>Type</th>
                      <th>Quantité</th>
                      <th>Stock Avant</th>
                      <th>Stock Après</th>
                      <th>Utilisateur</th>
                      <th>Raison</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stockMovements.map(movement => (
                      <tr key={movement.id}>
                        <td>{movement.date_mouvement ? new Date(movement.date_mouvement).toLocaleDateString('fr-FR') : 'N/A'}</td>
                        <td>{movement.nom_produit}</td>
                        <td>
                          <span className={`mouvement-type ${movement.type_mouvement}`}>
                            {movement.type_mouvement === 'entree' ? '⬆️ Entrée' : '⬇️ Sortie'}
                          </span>
                        </td>
                        <td>{movement.quantite}</td>
                        <td>{movement.stock_avant}</td>
                        <td>{movement.stock_apres}</td>
                        <td>{movement.utilisateur}</td>
                        <td>{movement.raison === 'ajout_manuel' ? 'Ajout manuel' : 
                            movement.raison === 'commande' ? 'Commande' : 
                            movement.raison || 'N/A'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p>Aucun mouvement de stock enregistré.</p>
              )}
            </div>
            <div className="form-actions">
              <button 
                type="button" 
                onClick={() => setShowStockHistory(false)}
                className="btn btn-secondary"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      {showProduitForm && (
        <div className="modal-overlay">
          <div className="modal modal-large">
            <div className="modal-header">
              <h3>{editingProduit ? 'Modifier le produit' : 'Nouveau produit'}</h3>
              <button className="modal-close" onClick={() => {
                setShowProduitForm(false);
                setEditingProduit(null);
              }}>×</button>
            </div>
            <form onSubmit={handleProduitSubmit}>
              {/* Section Informations de base */}
              <div className="commande-section commande-client">
                <h4 className="section-title">🥕 Informations du Produit</h4>
                <div className="form-group">
                  <label>Nom du produit *</label>
                  <input 
                    type="text" 
                    name="nom" 
                    required 
                    defaultValue={editingProduit?.nom || ''}
                  />
                </div>
                <div className="form-group">
                  <label>Description</label>
                  <textarea 
                    name="description" 
                    defaultValue={editingProduit?.description || ''}
                  />
                </div>
                <div className="form-group">
                  <label>Famille/Catégorie</label>
                  <select name="famille" defaultValue={editingProduit?.famille || ''}>
                    <option value="">Sélectionnez une famille</option>
                    <option value="Légumes racines">Légumes racines</option>
                    <option value="Légumes feuilles">Légumes feuilles</option>
                    <option value="Légumes fruits">Légumes fruits</option>
                    <option value="Aromates">Aromates</option>
                    <option value="Autres">Autres</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="checkbox-label">
                    <input 
                      type="checkbox" 
                      name="bio" 
                      defaultChecked={editingProduit?.bio || false}
                    />
                    <span className="checkmark"></span>
                    🌿 Produit Biologique (BIO)
                  </label>
                </div>
                <div className="form-group">
                  <label className="checkbox-label">
                    <input 
                      type="checkbox" 
                      name="fin_de_saison" 
                      defaultChecked={editingProduit?.fin_de_saison || false}
                    />
                    <span className="checkmark"></span>
                    🍂 Fin de saison (produit grisé/désactivé)
                  </label>
                </div>
              </div>

              {/* Section Prix */}
              <div className="commande-section commande-produits">
                <h4 className="section-title">💰 Tarification</h4>
                <div className="form-group">
                  <label>Prix unitaire par défaut (€) *</label>
                  <input 
                    type="number" 
                    name="prix_unitaire" 
                    step="0.01" 
                    min="0" 
                    required 
                    defaultValue={editingProduit?.prix_unitaire || ''}
                  />
                </div>
                
                <div className="prix-par-type">
                  <h4>Prix spécifiques par type de client</h4>
                  <div className="prix-grid">
                  <div className="form-group">
                    <label>Prix {settings?.types_clients?.[0] || 'GMS'} (€)</label>
                    <input 
                      type="number" 
                      name="prix_gms" 
                      step="0.01" 
                      min="0" 
                      defaultValue={editingProduit?.prix_par_type?.prix_gms || ''}
                      placeholder="Prix par défaut si vide"
                    />
                  </div>
                  <div className="form-group">
                    <label>Prix {settings?.types_clients?.[1] || 'Boutique'} (€)</label>
                    <input 
                      type="number" 
                      name="prix_boutique" 
                      step="0.01" 
                      min="0" 
                      defaultValue={editingProduit?.prix_par_type?.prix_boutique || ''}
                      placeholder="Prix par défaut si vide"
                    />
                  </div>
                  <div className="form-group">
                    <label>Prix {settings?.types_clients?.[2] || 'Particulier'} (€)</label>
                    <input 
                      type="number" 
                      name="prix_particulier" 
                      step="0.01" 
                      min="0" 
                      defaultValue={editingProduit?.prix_par_type?.prix_particulier || ''}
                      placeholder="Prix par défaut si vide"
                    />
                  </div>
                </div>
              </div>
              </div>
              
              {/* Section Stock */}
              <div className="commande-section" style={{borderColor: '#28a745', background: 'linear-gradient(135deg, rgba(40, 167, 69, 0.05), rgba(40, 167, 69, 0.02))'}}>
                <h4 className="section-title">📦 Stock et Unités</h4>
                <div className="form-group">
                  <label>Unité *</label>
                  <select 
                    name="unite" 
                    required 
                    defaultValue={editingProduit?.unite || ''}
                  >
                    <option value="">Sélectionnez une unité</option>
                    <option value="kg">Kilogramme (kg)</option>
                    <option value="piece">Pièce</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Stock actuel *</label>
                  <input 
                    type="number" 
                    name="stock_actuel" 
                    step="0.01" 
                    min="0" 
                    required 
                    defaultValue={editingProduit?.stock_actuel || ''}
                  />
                </div>
                <div className="form-group">
                  <label>Seuil d'alerte minimum</label>
                  <input 
                    type="number" 
                    name="seuil_stock_min" 
                    step="0.01" 
                    min="0" 
                    defaultValue={editingProduit?.seuil_stock_min || ''}
                    placeholder={`Seuil par défaut: ${settings?.seuil_stock_defaut || 10}`}
                  />
                </div>
              </div>
              <div className="form-actions">
                <button type="submit" className="btn btn-primary">
                  {editingProduit ? 'Modifier' : 'Créer'}
                </button>
                <button 
                  type="button" 
                  onClick={() => {
                    setShowProduitForm(false);
                    setEditingProduit(null);
                  }}
                  className="btn btn-secondary"
                >
                  Annuler
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Filtres produits */}
      <div className="filters-section">
        <div className="filters-row">
          <div className="filter-group search-group">
            <label>🔍 Rechercher</label>
            <input 
              type="text"
              placeholder="Nom, description, famille..."
              value={searchTermProduits}
              onChange={(e) => setSearchTermProduits(e.target.value)}
              className="search-input"
            />
          </div>
          <div className="filter-group">
            <label>Filtrer par famille</label>
            <select 
              value={produitFamilleFilter} 
              onChange={(e) => setProduitFamilleFilter(e.target.value)}
              className="filter-select"
            >
              <option value="">Toutes les familles</option>
              <option value="Légumes racines">Légumes racines</option>
              <option value="Légumes feuilles">Légumes feuilles</option>
              <option value="Légumes fruits">Légumes fruits</option>
              <option value="Aromates">Aromates</option>
              <option value="Autres">Autres</option>
            </select>
          </div>
          <div className="filter-group">
            <button 
              onClick={() => {
                setSearchTermProduits('');
                setProduitFamilleFilter('');
                setCurrentPageProduits(1); // Retour à la première page
              }}
              className="btn btn-secondary btn-sm"
            >
              🔄 Réinitialiser
            </button>
          </div>
        </div>
      </div>

      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>Nom</th>
              <th>Famille</th>
              <th>Description</th>
              <th>Prix par défaut</th>
              <th>Prix {settings?.types_clients?.[0] || 'GMS'}</th>
              <th>Prix {settings?.types_clients?.[1] || 'Boutique'}</th>
              <th>Prix {settings?.types_clients?.[2] || 'Particulier'}</th>
              <th>Unité</th>
              <th>Stock</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {getPaginatedItems(
              getFilteredProduits(), 
              currentPageProduits, 
              itemsPerPage
            ).map(produit => {
              const seuil = produit.seuil_stock_min || settings?.seuil_stock_defaut || 10;
              const isLowStock = produit.stock_actuel <= seuil;
              const isCritical = produit.stock_actuel <= seuil * 0.5;
              
              return (
                <tr key={produit.id} className={produit.fin_de_saison ? 'fin-de-saison' : ''}>
                  <td>
                    {produit.nom}
                    {produit.bio && (
                      <span className="bio-badge">🌿 BIO</span>
                    )}
                    {produit.fin_de_saison && (
                      <span className="fin-saison-badge">🍂 FIN DE SAISON</span>
                    )}
                  </td>
                  <td>
                    <span className={`type-badge ${produit.famille?.toLowerCase() || 'autres'}`}>
                      {produit.famille || 'Non définie'}
                    </span>
                  </td>
                  <td>{produit.description}</td>
                  <td>{produit.prix_unitaire.toFixed(2)} €</td>
                  <td>{produit.prix_par_type?.prix_gms?.toFixed(2) || produit.prix_unitaire.toFixed(2)} €</td>
                  <td>{produit.prix_par_type?.prix_boutique?.toFixed(2) || produit.prix_unitaire.toFixed(2)} €</td>
                  <td>{produit.prix_par_type?.prix_particulier?.toFixed(2) || produit.prix_unitaire.toFixed(2)} €</td>
                  <td>{produit.unite}</td>
                  <td className={isCritical ? 'critical-stock' : isLowStock ? 'low-stock' : ''}>
                    {formatNumber(produit.stock_actuel)} {produit.unite}
                    {isLowStock && (
                      <span className="stock-warning">
                        {isCritical ? '🚨' : '⚠️'}
                      </span>
                    )}
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button 
                        onClick={() => {
                          setEditingProduit(produit);
                          setShowProduitForm(true);
                        }}
                        className="btn-action btn-edit"
                        title="Modifier le produit"
                      >
                        Modifier
                      </button>
                      <button 
                        onClick={() => {
                          setSelectedProductForHistory(produit);
                          setShowStockHistory(true);
                          loadStockMovements(produit.id);
                        }}
                        className="btn-action btn-view"
                        title="Voir l'historique"
                      >
                        Historique
                      </button>
                      <button 
                        onClick={() => deleteProduit(produit.id)}
                        className="btn-action btn-delete"
                        title="Supprimer le produit"
                      >
                        Supprimer
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {getFilteredProduits().length === 0 && (
              <tr>
                <td colSpan="10" style={{textAlign: 'center', padding: '2rem', color: '#6c757d'}}>
                  {produitFamilleFilter || searchTermProduits ? 
                    'Aucun produit trouvé avec ces filtres' : 
                    'Aucun produit enregistré'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      
      {/* Pagination des produits */}
      <Pagination 
        items={getFilteredProduits()}
        currentPage={currentPageProduits}
        setCurrentPage={setCurrentPageProduits}
        itemsPerPage={itemsPerPage}
        setItemsPerPage={setItemsPerPage}
      />
    </div>
  );

  const renderCommandes = () => (
    <div className="commandes">
      <div className="section-header">
        <h2>Gestion des Commandes</h2>
        <div className="header-buttons">
          <button 
            onClick={() => openCommandeForm()}
            className="btn btn-primary"
          >
            Nouvelle Commande
          </button>
        </div>
      </div>

      {showCommandeForm && (
        <div className="modal-overlay">
          <div className="modal modal-large">
            <div className="modal-header">
              <h3>{editingCommande ? 'Modifier la commande' : 'Nouvelle commande'}</h3>
              <button className="modal-close" onClick={() => {
                setShowCommandeForm(false);
                setEditingCommande(null);
              }}>×</button>
            </div>
            <form onSubmit={handleCommandeSubmit}>
              {/* Section Client */}
              <div className="commande-section commande-client">
                <h4 className="section-title">👤 Informations Client</h4>
                <div className="form-group">
                  <label>Client *</label>
                  <select 
                    name="client_id" 
                    id="client-select"
                    required 
                    defaultValue={editingCommande?.client_id || ''}
                    onChange={(e) => {
                      // Mettre à jour les prix pour tous les produits sélectionnés
                      const selects = document.querySelectorAll('.produit-select');
                      selects.forEach(select => {
                        if (select.value) {
                          updatePrixDefaut({target: select});
                        }
                      });
                    }}
                  >
                    <option value="">Sélectionnez un client</option>
                    {clients.map(client => (
                      <option key={client.id} value={client.id}>
                        {client.nom_entreprise} - {getDisplayClientType(client.type_client)}
                      </option>
                    ))}
                  </select>
                </div>
                
                {/* Section Date de commande */}
                <div className="form-group">
                  <label>Date de la commande {!editingCommande && '(optionnel)'}</label>
                  <input 
                    type="date" 
                    name="date_commande"
                    defaultValue={
                      editingCommande?.date_commande 
                        ? (() => {
                            // Extraire la date sans conversion de fuseau horaire
                            const dateStr = editingCommande.date_commande;
                            if (dateStr.includes('T')) {
                              // Date ISO, extraire juste la partie date
                              return dateStr.split('T')[0];
                            } else {
                              // Date déjà au format YYYY-MM-DD
                              return dateStr;
                            }
                          })()
                        : ''
                    }
                  />
                  <small className="form-text">
                    {editingCommande 
                      ? "Date de la commande existante. Vous pouvez la modifier si nécessaire."
                      : "Si non spécifiée, la date d'aujourd'hui sera utilisée."
                    }
                  </small>
                </div>
              </div>

              {/* Section Produits - React State Pure */}
              <div className="commande-section commande-produits">
                <h4 className="section-title">Produits</h4>
                <div className="produits-commande">
                  {lignesProduits.map((ligne, index) => (
                    <div key={ligne.id} className="produit-ligne">
                      <div className="produit-ligne-header">
                        <h5>Produit {index + 1}</h5>
                        {lignesProduits.length > 1 && (
                          <button 
                            type="button" 
                            className="btn-remove-line" 
                            onClick={() => supprimerLigneProduit(index)}
                            title="Supprimer cette ligne"
                          >
                            🗑️
                          </button>
                        )}
                      </div>
                      <div className="form-group">
                        <label>Produit</label>
                        <select 
                          value={ligne.produit_id}
                          onChange={(e) => {
                            updateLigneProduit(index, 'produit_id', e.target.value);
                            // Mettre à jour le prix par défaut
                            const selectedProduit = produits.find(p => p.id === e.target.value);
                            if (selectedProduit) {
                              let prixDefaut = selectedProduit.prix_unitaire;
                              // Calculer le prix selon le type de client sélectionné
                              const clientSelected = clients.find(c => c.id === document.querySelector('select[name="client_id"]')?.value);
                              if (selectedProduit.prix_par_type && clientSelected && settings?.types_clients) {
                                const typeIndex = settings.types_clients.findIndex(type => 
                                  type.toLowerCase() === clientSelected.type_client.toLowerCase()
                                );
                                switch (typeIndex) {
                                  case 0:
                                    prixDefaut = selectedProduit.prix_par_type.prix_gms || selectedProduit.prix_unitaire;
                                    break;
                                  case 1:
                                    prixDefaut = selectedProduit.prix_par_type.prix_boutique || selectedProduit.prix_unitaire;
                                    break;
                                  case 2:
                                    prixDefaut = selectedProduit.prix_par_type.prix_particulier || selectedProduit.prix_unitaire;
                                    break;
                                }
                              }
                              // Stocker le prix par défaut pour affichage
                              const nouvelleLigne = [...lignesProduits];
                              nouvelleLigne[index].prix_defaut = prixDefaut;
                              setLignesProduits(nouvelleLigne);
                            }
                          }}
                          className="produit-select"
                        >
                          <option value="">Sélectionnez un produit</option>
                          {produits.filter(p => !p.fin_de_saison).map(p => (
                            <option key={p.id} value={p.id}>
                              {p.nom} - {p.prix_unitaire.toFixed(2)}€/{p.unite}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="form-group">
                        <label>Quantité</label>
                        <input 
                          type="number" 
                          step="0.01" 
                          min="0"
                          value={ligne.quantite}
                          onChange={(e) => updateLigneProduit(index, 'quantite', e.target.value)}
                        />
                      </div>
                      <div className="form-group">
                        <label>Prix personnalisé (€)</label>
                        <input 
                          type="number" 
                          step="0.01" 
                          min="0"
                          value={ligne.prix_personnalise}
                          onChange={(e) => updateLigneProduit(index, 'prix_personnalise', e.target.value)}
                          placeholder="Prix automatique" 
                        />
                      </div>
                      <div className="prix-info">
                        <span className="prix-defaut">
                          Prix par défaut: {ligne.prix_defaut?.toFixed(2) || '--'} €
                        </span>
                      </div>
                    </div>
                  ))}
                  <button type="button" className="btn btn-secondary btn-sm" onClick={ajouterLigneProduit}>
                    Ajouter un produit
                  </button>
                </div>
              </div>

              {/* Section Frais de livraison */}
              <div className="commande-section commande-frais">
                <h4 className="section-title">🚚 Frais de livraison</h4>
                <div className="form-group">
                  <label>Frais de livraison (€)</label>
                  <input 
                    type="number" 
                    name="frais_livraison" 
                    step="0.01" 
                    min="0" 
                    defaultValue={editingCommande?.frais_livraison || 0}
                    placeholder="0.00"
                  />
                  <small className="form-text">
                    Les frais de livraison sont soumis à une TVA de 20%.
                  </small>
                </div>
              </div>
              
              <div className="form-actions">
                <button type="submit" className="btn btn-primary">
                  {editingCommande?.statut === 'brouillon' 
                    ? 'Créer la commande' 
                    : editingCommande 
                      ? 'Modifier la commande' 
                      : 'Créer la commande'}
                </button>
                {!editingCommande && (
                  <button 
                    type="button" 
                    onClick={saveBrouillon}
                    className="btn btn-secondary"
                    title="Sauvegarder comme brouillon"
                  >
                    Sauvegarder brouillon
                  </button>
                )}
                {editingCommande?.statut === 'brouillon' && (
                  <button 
                    type="button" 
                    onClick={saveBrouillon}
                    className="btn btn-secondary"
                    title="Mettre à jour le brouillon"
                  >
                    Sauvegarder brouillon
                  </button>
                )}
                <button 
                  type="button" 
                  onClick={closeCommandeForm}
                  className="btn btn-secondary"
                >
                  Annuler
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Filtres */}
      <div className="filters">
        <div className="filter-group search-group">
          <label>Rechercher par numéro :</label>
          <input 
            type="text"
            value={commandeSearchTerm}
            onChange={(e) => setCommandeSearchTerm(e.target.value)}
            placeholder="N° commande, facture ou avoir..."
            className="form-control"
          />
        </div>
        <div className="filter-group">
          <label>Filtrer par client :</label>
          <select 
            value={commandeClientFilter} 
            onChange={(e) => setCommandeClientFilter(e.target.value)}
            className="form-control"
          >
            <option value="">Tous les clients</option>
            {clients.map(client => (
              <option key={client.id} value={client.id}>
                {client.nom_entreprise}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>N° Commande</th>
              <th>Client</th>
              <th>Date</th>
              <th>Produits</th>
              <th>Total</th>
              <th>Statut</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {getPaginatedItems(
              getFilteredCommandes(), 
              currentPageCommandes, 
              itemsPerPage
            ).map(commande => (
              <tr key={commande.id}>
                <td>{commande.id.substring(0, 8)}</td>
                <td>{commande.nom_client}</td>
                <td>{commande.date_commande ? new Date(commande.date_commande).toLocaleDateString('fr-FR') : 'N/A'}</td>
                <td>
                  <div className="produits-list">
                    {commande.produits.map((p, index) => (
                      <span key={index} className="produit-item">
                        {p.nom_produit} ({p.quantite} {p.unite})
                        {p.prix_unitaire !== p.prix_defaut && (
                          <span className="prix-personnalise">*</span>
                        )}
                      </span>
                    ))}
                  </div>
                </td>
                <td>{commande.total.toFixed(2)} €</td>
                <td>
                  <select 
                    value={commande.statut}
                    onChange={(e) => updateCommandeStatut(commande.id, e.target.value)}
                    className={`status-select ${commande.statut}`}
                  >
                    <option value="brouillon">Brouillon</option>
                    <option value="en_cours">En cours</option>
                    <option value="livre">Livrée</option>
                    <option value="facture">Facturée</option>
                    <option value="annule">Annulée</option>
                  </select>
                </td>
                <td>
                  <div className="action-buttons">
                    <button 
                      onClick={() => {
                        openCommandeForm(commande);
                      }}
                      className="btn-action btn-edit"
                      title="Modifier la commande"
                    >
                      Modifier
                    </button>
                    <button 
                      onClick={() => genererBonLivraison(commande.id)}
                      className="btn-action btn-view"
                      title="Bon de livraison"
                    >
                      Bon
                    </button>
                    {(commande.statut === 'facture' || (commande.statut === 'annule' && commande.numero_facture)) && (
                      <button 
                        onClick={() => genererFacture(commande.id)}
                        className="btn-action btn-secondary"
                        title={commande.numero_facture ? `Facture ${commande.numero_facture}` : "Générer/Télécharger facture"}
                      >
                        {commande.numero_facture || 'Facture'}
                      </button>
                    )}
                    {commande.statut === 'annule' && (
                      <button 
                        onClick={() => genererAvoir(commande.id)}
                        className="btn-action btn-danger"
                        title="Générer avoir"
                      >
                        {commande.numero_avoir || 'Avoir'}
                      </button>
                    )}
                    {canDeleteCommande(commande) && (
                      <button 
                        onClick={() => deleteCommande(commande.id)}
                        className="btn-action btn-delete"
                        title="Supprimer la commande"
                      >
                        Supprimer
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      
      {/* Pagination des commandes */}
      <Pagination 
        items={getFilteredCommandes()}
        currentPage={currentPageCommandes}
        setCurrentPage={setCurrentPageCommandes}
        itemsPerPage={itemsPerPage}
        setItemsPerPage={setItemsPerPage}
      />
    </div>
  );

  const renderStatistiques = () => (
    <div className="statistiques">
      <div className="section-header">
        <h2>Statistiques des Ventes</h2>
        <button 
          onClick={() => {
            loadStatistiques();
            loadStatistiquesProduits();
          }}
          className="btn btn-primary"
        >
          🔄 Actualiser
        </button>
      </div>

      {statistiques && (
        <div>
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-icon">📋</div>
              <div className="stat-content">
                <h3>Commandes Traitées</h3>
                <p className="stat-number">{statistiques.total_commandes}</p>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">💰</div>
              <div className="stat-content">
                <h3>Chiffre d'Affaires</h3>
                <p className="stat-number">{parseFloat(statistiques.total_ca).toFixed(2)} €</p>
              </div>
            </div>
          </div>

          <div className="stats-section">
            <h3>📈 Évolution Quotidienne des Ventes</h3>
            <p className="section-subtitle">Aperçu des 10 derniers jours d'activité</p>
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Commandes</th>
                    <th>Chiffre d'Affaires</th>
                  </tr>
                </thead>
                <tbody>
                  {statistiques.stats_par_jour.slice(-10).map(stat => (
                    <tr key={stat.date}>
                      <td>{stat.jour}</td>
                      <td>{stat.commandes}</td>
                      <td>{parseFloat(stat.ca).toFixed(2)} €</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="stats-section">
            <h3>📊 Performance Mensuelle</h3>
            <p className="section-subtitle">Analyse des tendances sur 12 mois glissants</p>
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Mois</th>
                    <th>Commandes</th>
                    <th>Chiffre d'Affaires</th>
                  </tr>
                </thead>
                <tbody>
                  {statistiques.stats_par_mois.map(stat => (
                    <tr key={stat.date}>
                      <td>{stat.nom_mois}</td>
                      <td>{stat.commandes}</td>
                      <td>{parseFloat(stat.ca).toFixed(2)} €</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="stats-section">
            <h3>🗓️ Bilan Annuel</h3>
            <p className="section-subtitle">Vue d'ensemble des résultats par année</p>
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Année</th>
                    <th>Commandes</th>
                    <th>Chiffre d'Affaires</th>
                  </tr>
                </thead>
                <tbody>
                  {statistiques.stats_par_annee.map(stat => (
                    <tr key={stat.annee}>
                      <td>{stat.annee}</td>
                      <td>{stat.commandes}</td>
                      <td>{parseFloat(stat.ca).toFixed(2)} €</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Section statistiques par produit */}
          {statistiquesProduits && (
            <div className="stats-section">
              <h3>🥕 Analyse par Produit</h3>
              <p className="section-subtitle">Classement des produits les plus performants • Période : {statistiquesProduits.periode}</p>
              <div className="table-container">
                <table>
                  <thead>
                    <tr>
                      <th>Produit</th>
                      <th>Quantité Totale</th>
                      <th>Nombre de Commandes</th>
                      <th>Chiffre d'Affaires</th>
                    </tr>
                  </thead>
                  <tbody>
                    {statistiquesProduits.stats_par_produit.map((produit, index) => (
                      <tr key={index}>
                        <td>{produit.nom_produit}</td>
                        <td>{formatNumber(produit.quantite_totale)}</td>
                        <td>{produit.nombre_commandes}</td>
                        <td>{produit.chiffre_affaires.toFixed(2)} €</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );

  const renderReglages = () => (
    <div className="reglages">
      <div className="section-header">
        <h2>Paramètres de l'Application</h2>
      </div>

      {settings && (
        <div className="settings-container">
          <form onSubmit={handleSettingsSubmit} className="settings-form">
            <div className="settings-section">
              <h3>🏢 Informations de l'Entreprise</h3>
              <div className="form-group">
                <label>Nom de l'entreprise</label>
                <input 
                  type="text" 
                  name="nom_entreprise" 
                  defaultValue={settings.nom_entreprise}
                  required 
                />
              </div>
              <div className="form-group">
                <label>Adresse</label>
                <textarea 
                  name="adresse" 
                  defaultValue={settings.adresse}
                  required 
                />
              </div>
              <div className="form-group">
                <label>SIRET</label>
                <input 
                  type="text" 
                  name="siret" 
                  defaultValue={settings.siret || ''}
                  placeholder="Numéro SIRET de l'entreprise"
                />
              </div>
              <div className="form-group">
                <label>N° TVA intracommunautaire</label>
                <input 
                  type="text" 
                  name="numero_tva_intra" 
                  defaultValue={settings.numero_tva_intra || ''}
                  placeholder="FR12345678901 (optionnel, pour export UE)"
                />
              </div>
              <div className="form-group">
                <label>Numéro Ecocert</label>
                <input 
                  type="text" 
                  name="numero_ecocert" 
                  defaultValue={settings.numero_ecocert || ''}
                  placeholder="Numéro de certification Ecocert (pour produits bio)"
                />
              </div>
              <div className="form-group">
                <label>IBAN</label>
                <textarea 
                  name="numero_iban" 
                  defaultValue={settings.numero_iban || ''}
                  placeholder="IBAN de l'entreprise (apparaîtra en bas des factures)"
                  rows="3"
                />
                <small className="form-text">
                  L'IBAN apparaîtra en bas des factures pour les paiements clients
                </small>
              </div>
              <div className="form-group">
                <label>Capital Social</label>
                <input 
                  type="text" 
                  name="capital_social" 
                  defaultValue={settings.capital_social || ''}
                  placeholder="Ex: 10 000 EUR"
                />
                <small className="form-text">
                  Le capital social apparaîtra dans les informations de l'entreprise sur les factures
                </small>
              </div>
              <div className="form-group">
                <label className="checkbox-label">
                  <input 
                    type="checkbox" 
                    name="en_conversion_bio" 
                    defaultChecked={settings.en_conversion_bio || false}
                  />
                  <span className="checkmark"></span>
                  🌱 En conversion à l'agriculture biologique
                </label>
                <p className="form-text">Cette mention apparaîtra sur les factures et bons de livraison. Décochez une fois la conversion terminée.</p>
              </div>
              <div className="form-group">
                <label>Seuil d'alerte de stock par défaut</label>
                <input 
                  type="number" 
                  name="seuil_stock_defaut" 
                  step="0.01" 
                  min="0" 
                  defaultValue={settings.seuil_stock_defaut || 10}
                  required 
                />
                <small className="form-text">
                  Cette valeur sera utilisée pour les produits sans seuil personnalisé
                </small>
              </div>
              <div className="form-group toggle-group">
                <div className="toggle-container">
                  <div className="toggle-header">
                    <label htmlFor="gestion_stock_active" className="toggle-title">
                      Gestion du stock pour les commandes
                    </label>
                    <div className="toggle-switch">
                      <input 
                        type="checkbox" 
                        id="gestion_stock_active"
                        name="gestion_stock_active" 
                        defaultChecked={settings.gestion_stock_active !== false}
                        className="toggle-checkbox"
                      />
                      <label htmlFor="gestion_stock_active" className="toggle-label">
                        <span className="toggle-slider"></span>
                      </label>
                    </div>
                  </div>
                  <div className="toggle-description">
                    <p className="toggle-text-active">
                      <strong>Activé :</strong> Les commandes vérifieront et décrémenteront automatiquement le stock
                    </p>
                    <p className="toggle-text-inactive">
                      <strong>Désactivé :</strong> Les commandes pourront être créées sans limite de stock
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="settings-section">
              <h3>🎨 Apparence</h3>
              <div className="form-group color-group">
                <label>
                  <div className="color-preview" style={{backgroundColor: settings?.couleur_primaire || '#8CC63F'}}></div>
                  Couleur principale
                </label>
                <div className="color-input-group">
                  <input 
                    type="color" 
                    name="couleur_primaire" 
                    defaultValue={settings.couleur_primaire}
                    className="color-picker"
                    onChange={(e) => {
                      const textInput = e.target.parentElement.querySelector('.color-text');
                      const preview = e.target.parentElement.parentElement.querySelector('.color-preview');
                      textInput.value = e.target.value;
                      preview.style.backgroundColor = e.target.value;
                    }}
                  />
                  <input 
                    type="text" 
                    defaultValue={settings.couleur_primaire}
                    className="color-text"
                    placeholder="#8CC63F"
                    pattern="^#[0-9A-Fa-f]{6}$"
                    onChange={(e) => {
                      const colorPicker = e.target.previousSibling;
                      const preview = e.target.parentElement.parentElement.querySelector('.color-preview');
                      if (e.target.value.match(/^#[0-9A-Fa-f]{6}$/)) {
                        colorPicker.value = e.target.value;
                        preview.style.backgroundColor = e.target.value;
                      }
                    }}
                  />
                </div>
                <p className="form-text">Choisissez la couleur principale de votre interface</p>
              </div>
              <div className="form-group">
                <label>Logo de l'entreprise</label>
                <div className="logo-upload-group">
                  {settings.logo_url && (
                    <img src={settings.logo_url} alt="Logo" className="current-logo" />
                  )}
                  <input 
                    type="file" 
                    accept="image/*"
                    onChange={handleLogoUpload}
                    className="file-input"
                    id="logo-upload"
                  />
                  <label htmlFor="logo-upload" className="btn btn-secondary">
                    📁 Changer le logo
                  </label>
                </div>
              </div>
            </div>

            <div className="settings-section">
              <h3>👥 Types de Clients</h3>
              <div className="client-types-grid">
                <div className="form-group">
                  <label>Type 1</label>
                  <input 
                    type="text" 
                    name="type_client_1" 
                    defaultValue={settings.types_clients[0] || ''}
                    placeholder="Ex: GMS"
                  />
                </div>
                <div className="form-group">
                  <label>Type 2</label>
                  <input 
                    type="text" 
                    name="type_client_2" 
                    defaultValue={settings.types_clients[1] || ''}
                    placeholder="Ex: boutique"
                  />
                </div>
                <div className="form-group">
                  <label>Type 3</label>
                  <input 
                    type="text" 
                    name="type_client_3" 
                    defaultValue={settings.types_clients[2] || ''}
                    placeholder="Ex: particulier"
                  />
                </div>
              </div>
            </div>

            <div className="form-actions">
              <button type="submit" className="btn btn-primary">
                Sauvegarder les paramètres
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );

  const renderUsers = () => (
    <div className="users">
      <div className="section-header">
        <h2>Gestion des Utilisateurs</h2>
        <button 
          onClick={() => setShowUserForm(true)}
          className="btn btn-primary"
        >
          Nouvel Utilisateur
        </button>
      </div>

      {showUserForm && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>Nouvel utilisateur</h3>
            <form onSubmit={handleUserSubmit}>
              <div className="form-group">
                <label>Nom d'utilisateur *</label>
                <input 
                  type="text" 
                  name="username" 
                  required 
                />
              </div>
              <div className="form-group">
                <label>Email *</label>
                <input 
                  type="email" 
                  name="email" 
                  required 
                />
              </div>
              <div className="form-group">
                <label>Mot de passe *</label>
                <input 
                  type="password" 
                  name="password" 
                  required 
                />
              </div>
              <div className="form-group">
                <label>Rôle *</label>
                <select name="role" required>
                  <option value="user">Utilisateur</option>
                  <option value="admin">Administrateur</option>
                </select>
              </div>
              <div className="form-actions">
                <button type="submit" className="btn btn-primary">
                  Créer
                </button>
                <button 
                  type="button" 
                  onClick={() => setShowUserForm(false)}
                  className="btn btn-secondary"
                >
                  Annuler
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>Nom d'utilisateur</th>
              <th>Email</th>
              <th>Rôle</th>
              <th>Statut</th>
              <th>Date de création</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map(user => (
              <tr key={user.id}>
                <td>{user.username}</td>
                <td>{user.email}</td>
                <td>
                  <span className={`role-badge ${user.role}`}>
                    {user.role === 'admin' ? 'Administrateur' : 'Utilisateur'}
                  </span>
                </td>
                <td>
                  <span className={`status ${user.active ? 'active' : 'inactive'}`}>
                    {user.active ? 'Actif' : 'Inactif'}
                  </span>
                </td>
                <td>{user.created_at ? new Date(user.created_at).toLocaleDateString('fr-FR') : 'N/A'}</td>
                <td>
                  {currentUser?.username !== user.username && (
                    <div className="user-actions">
                      <button 
                        onClick={() => resetUserPassword(user.id, user.username)}
                        className="btn-action btn-primary"
                        title="Réinitialiser le mot de passe"
                      >
                        🔄 Reset MDP
                      </button>
                      <select 
                        value={user.role}
                        onChange={(e) => changeUserRole(user.id, e.target.value)}
                        className="role-select"
                        title="Changer le rôle"
                      >
                        <option value="user">Utilisateur</option>
                        <option value="admin">Administrateur</option>
                      </select>
                      <button 
                        onClick={() => toggleUserActive(user.id, user.active)}
                        className={`btn-action ${user.active ? 'btn-warning' : 'btn-success'}`}
                        title={user.active ? 'Désactiver' : 'Activer'}
                      >
                        {user.active ? '⏸️ Désactiver' : '▶️ Activer'}
                      </button>
                      <button 
                        onClick={() => deleteUser(user.id)}
                        className="btn-action btn-danger"
                        title="Supprimer définitivement"
                      >
                        🗑️ Supprimer
                      </button>
                    </div>
                  )}
                  {currentUser?.username === user.username && (
                    <span className="current-user-badge">C'est vous</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <div className="App">
      <header className="app-header">
        <div className="header-content">
          <div className="header-title">
            {settings?.logo_url ? (
              <div className="header-logo">
                <img src={settings.logo_url} alt={settings?.nom_entreprise || 'Ferme de Larrue'} className="logo-img" />
                <p>Gestion Maraîcher - {settings?.adresse || '2 rue de Larrue, 77320 CHEVRU'}</p>
              </div>
            ) : (
              <>
                <h1>🌱 {settings?.nom_entreprise || 'Ferme de Larrue'}</h1>
                <p>Gestion Maraîcher - {settings?.adresse || '2 rue de Larrue, 77320 CHEVRU'}</p>
              </>
            )}
          </div>
          <div className="header-actions">
            <button 
              className="mobile-menu-toggle"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              ☰
            </button>
            
            {/* Boutons admin uniquement */}
            {currentUser?.role === 'admin' && (
              <button 
                onClick={() => handleTabChange('reglages')}
                className="header-btn"
              >
                ⚙️ Réglages
              </button>
            )}
            <button onClick={handleLogout} className="header-btn">
              🚪 Déconnexion
            </button>
          </div>
        </div>
      </header>

      <div className="nav-container">
        <nav className={`nav-tabs ${isMobileMenuOpen ? 'mobile-open' : ''}`}>
        <button 
          className={activeTab === 'dashboard' ? 'active' : ''}
          onClick={() => handleTabChange('dashboard')}
        >
          Tableau de bord
        </button>
        <button 
          className={activeTab === 'clients' ? 'active' : ''}
          onClick={() => handleTabChange('clients')}
        >
          Clients
        </button>
        <button 
          className={activeTab === 'produits' ? 'active' : ''}
          onClick={() => handleTabChange('produits')}
        >
          Produits
        </button>
        <button 
          className={activeTab === 'commandes' ? 'active' : ''}
          onClick={() => handleTabChange('commandes')}
        >
          Commandes
        </button>
        <button 
          className={activeTab === 'statistiques' ? 'active' : ''}
          onClick={() => handleTabChange('statistiques')}
        >
          Statistiques
        </button>
        {currentUser?.role === 'admin' && (
          <button 
            className={activeTab === 'users' ? 'active' : ''}
            onClick={() => handleTabChange('users')}
          >
            Utilisateurs
          </button>
        )}
      </nav>
      </div>

      <main className="main-content">
        {activeTab === 'dashboard' && renderDashboard()}
        {activeTab === 'clients' && renderClients()}
        {activeTab === 'produits' && renderProduits()}
        {activeTab === 'commandes' && renderCommandes()}
        {activeTab === 'statistiques' && renderStatistiques()}
        {activeTab === 'reglages' && currentUser?.role === 'admin' && renderReglages()}
        {activeTab === 'users' && currentUser?.role === 'admin' && renderUsers()}
      </main>

      {/* Zone de notifications toast */}
      <div className="notifications-container">
        {notifications.map(notification => (
          <NotificationToast
            key={notification.id}
            notification={notification}
            onRemove={removeNotification}
          />
        ))}
      </div>

      {/* Pied de page */}
      <footer className="footer">
        <p>
          Made with ❤️ by <a href="https://www.youdemus.fr" target="_blank" rel="noopener noreferrer">Youdemus</a>
          {' | '}
          <a href="https://www.maraichpro.com" target="_blank" rel="noopener noreferrer">Support & Documentation</a>
        </p>
      </footer>

      {/* NOUVEAU: Bouton Back to Top */}
      {showBackToTop && (
        <button 
          className="back-to-top visible"
          onClick={scrollToTop}
          title="Retour en haut"
        >
          ↑
        </button>
      )}

      {/* NOUVEAU: Modal PDF pour mobiles/iPad */}
      {showMobilePDF && (
        <div className="mobile-pdf-modal">
          <div className="mobile-pdf-header">
            <h3>{mobilePDFTitle}</h3>
            <div className="mobile-pdf-actions">
              <button 
                className="mobile-pdf-print"
                onClick={printMobilePDF}
              >
                🖨️ Imprimer
              </button>
              <button 
                className="mobile-pdf-close"
                onClick={() => setShowMobilePDF(false)}
              >
                ✕ Fermer
              </button>
            </div>
          </div>
          <div className="mobile-pdf-content">
            <iframe 
              srcDoc={mobilePDFContent}
              style={{
                width: '100%',
                height: '100%',
                border: 'none'
              }}
              title={mobilePDFTitle}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
